import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { collection, getDocs, query, where, Timestamp, orderBy, limit, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import * as XLSX from "xlsx";
import { getFieldLabel } from "../utils/fieldLabels";
import { buildFuelRowsWithDeltaKm } from "../../../shared/fuelDeltaKm";
import { getAuth } from "firebase/auth"; // Bu satırı ekle

const PLATE_FIELD_CANDIDATES = ["vehiclePlate", "plaka", "mikserPlaka", "pompaPlaka", "plate"];
const FUEL_PLATE_FIELD_CANDIDATES = ["vehiclePlate", "plaka", "plate"];
const PERSON_FIELD_CANDIDATES = ["personelAdi", "soforAdi", "driverName", "operatorAdi", "operator", "yagciAdi"];
const TESIS_FIELD_CANDIDATES = ["Tesis", "tesis", "Tesis_Adı", "vehicleTesis"];
const DATE_FIELD_CANDIDATES = ["date", "tarih", "createdAt", "updatedAt"];
const RECEIPT_FIELD_CANDIDATES = ["receiptNo", "fisNo", "fişNo", "faturaNo", "voucherNo"];
const ODOMETER_FIELD_CANDIDATES = ["odometerKm", "km", "kilometre"];
const DELTA_KM_FIELD_CANDIDATES = ["deltaKm", "farkKm", "kmFark", "farkKM", "kmDelta", "Fark KM"];
const HAKEDIS_SOZLESME_PREFIX = "Hakediş - ";
const SOURCE_DEDUP_FIELDS = new Set(["m3", "fiiliM3", "votoKm"]);
const BASE_DIMENSION_FIELDS = ["Gün", "GÃ¼n", "Hafta", "Tesis", "personelAdi", "personelGorevi", "soforAdi", "mikserPlaka", "pompaPlaka", "plaka"];
const BASE_MEASURE_FIELDS = ["m3", "fiiliM3", "votoKm", "Toplam KM", "Sefer Sayısı", "Sefer SayÄ±sÄ±", "Hakediş", "HakediÅŸ", "Mazot"];
const SOURCE_FIELD_CATALOG = {
  hazirbeton_mikser_sevkiyat: {
    dimensions: ["tesis", "Tesis_AdÄ±", "tarih", "irsaliyeNo", "musteriAdi", "santiyeAdi", "mikserPlaka", "teslimSekli", "pompaPlaka", "soforAdi", "mikserFirmasi", "pompaFirmasi", "GÃ¼n", "Hafta", "Tesis"],
    measures: ["m3", "fiiliM3", "uretimM3_zone", "votoKm", "cikisKm", "donusKm", "fiiliKm", "Sefer SayÄ±sÄ±", "HakediÅŸ", "Toplam KM"]
  },
  hazirbeton_pompa_hakedis: {
    dimensions: ["tesis", "Tesis_AdÄ±", "tarih", "pompaPlaka", "operatorAdi", "yagciAdi", "operator", "musteriAdi", "santiyeAdi", "GÃ¼n", "Hafta", "Tesis"],
    measures: ["m3", "fiiliM3", "votoKm", "Sefer SayÄ±sÄ±", "HakediÅŸ", "Toplam KM"]
  },
  fuel_transactions: {
    dimensions: ["date", "tarih", "receiptNo", "vehiclePlate", "driverName", "driverId", "tesis", "Tesis", "GÃ¼n", "Hafta"],
    measures: ["liters", "odometerKm", "Toplam KM"]
  },
  plates: {
    dimensions: ["plate", "plaka", "vehiclePlate", "aracCinsi", "vehicleType", "marka", "model", "ustGrupMarka", "ustGrupModel", "bomUzunlugu", "bomUzunluÄŸu", "tesis"],
    measures: []
  }
};
const FORMAT_COLOR_PALETTE = [
  { label: "Kirmizi", value: "#fee2e2", text: "#991b1b" },
  { label: "Yesil", value: "#dcfce7", text: "#166534" },
  { label: "Sari", value: "#fef3c7", text: "#92400e" },
  { label: "Mavi", value: "#dbeafe", text: "#1e40af" },
  { label: "Mor", value: "#ede9fe", text: "#5b21b6" },
  { label: "Turkuaz", value: "#ccfbf1", text: "#115e59" },
  { label: "Pembe", value: "#fce7f3", text: "#9d174d" },
  { label: "Gri", value: "#f1f5f9", text: "#334155" }
];

const getHakedisSozlesmeKolonu = (baslik) => `${HAKEDIS_SOZLESME_PREFIX}${baslik || "Sözleşme"}`;

const makeCalcId = () => `calc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const FIELD_ALIASES = {
  "GÃ¼n": "Gün",
  "GÃƒÂ¼n": "Gün",
  "Sefer SayÄ±sÄ±": "Sefer Sayısı",
  "Sefer SayÃ„Â±sÃ„Â±": "Sefer Sayısı",
  "HakediÅŸ": "Hakediş",
  "HakediÃ…Å¸": "Hakediş",
  "Tesis_AdÄ±": "Tesis_Adı",
  "Tesis_AdÃ„Â±": "Tesis_Adı",
  "bomUzunluÄŸu": "bomUzunluğu",
  "bomUzunluÃ„Å¸u": "bomUzunluğu"
};

const canonicalFieldName = (fieldName = "") => FIELD_ALIASES[String(fieldName || "")] || String(fieldName || "");

const HakedisPivotEngine = () => {
  // --- İsim Dönüştürme Sözlüğü ---
  const alanIsimleri = {
    "m3": "Toplam m3",
    "Sefer Sayısı": "Sefer",
    "Hakediş": "Hakediş Tutarı",
    "Tesis": "Tesis",
    "Hafta": "Hafta",
    "Gün": "Gün",
    "plaka": "Plaka",
    "soforAdi": "Sürücü",
    "operator": "Operatör",
    "Satış_M3": "Satış m3",
    "Tesis_Adı": "Tesis",
    "pompaPlaka": "Pompa Plaka",
    "Toplam KM": "Toplam KM",
  };

  const [seciliAylar, setSeciliAylar] = useState([new Date().toISOString().slice(0, 7)]);
  const [seciliHaftalar, setSeciliHaftalar] = useState([]);
  const [seciliSozlesmeIds, setSeciliSozlesmeIds] = useState([]);
  const [sozlesmeler, setSozlesmeler] = useState([]);
  
  const [mevcutAlanlar, setMevcutAlanlar] = useState([]);
  const [satirlar, setSatirlar] = useState(["Tesis"]); 
  const [sutunlar, setSutunlar] = useState(["m3", "Sefer Sayısı", "Hakediş"]);
  
  const [hamVeri, setHamVeri] = useState([]); 
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manuelMazotlar, setManuelMazotlar] = useState({});
  // --- TASARIM VE PRESET STATE'LERİ ---
const [designs, setDesigns] = useState([]);
const [selectedDesignId, setSelectedDesignId] = useState("");
const [reportName, setReportName] = useState("");
const [preset, setPreset] = useState("OZEL");
const [sortedBy, setSortedBy] = useState({ col: null, asc: true });
const [showSubtotals, setShowSubtotals] = useState(false);
const [reportScope, setReportScope] = useState("personel");

const HAKEDIS_PRESETS = [
  { key: "BU_AY", label: "Bu Ay" },
  { key: "GECEN_AY", label: "Geçen Ay" },
  { key: "BU_HAFTA", label: "Bu Hafta" },
  { key: "GECEN_HAFTA", label: "Geçen Hafta" },
];

const [filters, setFilters] = useState({}); // { Plaka: ["06ABC01"], Tesis: ["Merkez"] }
const [openFilter, setOpenFilter] = useState(null); // Hangi kolonun filtresi açık?
const [filterSearch, setFilterSearch] = useState("");
const filterRef = useRef(null);
const [alanAra, setAlanAra] = useState("");
const [calculatedFields, setCalculatedFields] = useState([]);
const [calcDraft, setCalcDraft] = useState({
  name: "",
  formulaText: "",
  format: "number",
  decimals: 2
});
const [conditionalFormats, setConditionalFormats] = useState([]);
const [formatDraft, setFormatDraft] = useState({
  field: "",
  type: "greaterThan",
  value: "",
  value2: "",
  n: 3,
  backgroundColor: "#fee2e2",
  color: "#991b1b"
});

const measureAlanlari = useMemo(() => {
  const set = new Set(BASE_MEASURE_FIELDS);

  sozlesmeler.forEach(sozlesme => {
    set.add(getHakedisSozlesmeKolonu(sozlesme.baslik));
    (sozlesme.ekBaglantilar || []).forEach(ek => {
      if (ek?.etiket) set.add(ek.etiket);
      else if (ek?.koleksiyon && ek?.hedefAlan) set.add(`${ek.koleksiyon}.${ek.hedefAlan}`);
    });
  });

  calculatedFields.forEach(field => {
    if (field?.name) set.add(field.name);
  });

  return set;
}, [sozlesmeler, calculatedFields]);

const isMeasureField = useCallback((fieldName) => {
  const field = canonicalFieldName(fieldName);
  return measureAlanlari.has(field) || String(field || "").startsWith(HAKEDIS_SOZLESME_PREFIX);
}, [measureAlanlari]);

const cleanFieldName = (fieldName = "") => {
  const parts = String(fieldName || "").split(".");
  return canonicalFieldName(parts[parts.length - 1]);
};

const selectedSozlesmeler = useMemo(() => {
  return seciliSozlesmeIds
    .map(id => sozlesmeler.find(s => String(s.id) === String(id)))
    .filter(Boolean);
}, [seciliSozlesmeIds, sozlesmeler]);

const raporAlanMeta = useMemo(() => {
  const meta = new Map();
  const selectedCount = selectedSozlesmeler.length;

  const touch = (fieldName, kind, sozlesmeId = "__base__") => {
    const field = cleanFieldName(fieldName);
    if (!field || field.startsWith("_")) return;
    const current = meta.get(field) || { field, kind, contracts: new Set(), sources: new Set() };
    current.kind = current.kind === "measure" || kind === "measure" ? "measure" : kind;
    current.contracts.add(sozlesmeId);
    meta.set(field, current);
  };

  BASE_DIMENSION_FIELDS.forEach(field => touch(field, "dimension"));
  BASE_MEASURE_FIELDS.forEach(field => touch(field, "measure"));
  calculatedFields.forEach(field => {
    if (field?.name) touch(field.name, "measure");
  });

  selectedSozlesmeler.forEach(sozlesme => {
    const contractId = String(sozlesme.id || sozlesme.baslik || "");
    touch(getHakedisSozlesmeKolonu(sozlesme.baslik), "measure", contractId);

    const sourceNames = new Set((sozlesme.kurallar || []).map(kural => kural?.anaKaynak).filter(Boolean));
    if (sourceNames.size === 0) sourceNames.add("hazirbeton_pompa_hakedis");

    sourceNames.forEach(sourceName => {
      const catalog = SOURCE_FIELD_CATALOG[sourceName] || {};
      (catalog.dimensions || []).forEach(field => touch(field, "dimension", contractId));
      (catalog.measures || []).forEach(field => touch(field, "measure", contractId));
    });

    (sozlesme.kurallar || []).forEach(kural => {
      if (kural?.carpanAlan) touch(kural.carpanAlan, isMeasureField(kural.carpanAlan) ? "measure" : "dimension", contractId);
      (kural?.filtreler || []).forEach(filter => {
        if (filter?.alan) touch(filter.alan, "dimension", contractId);
      });
    });

    (sozlesme.ekBaglantilar || []).forEach(ek => {
      if (ek?.anaAlan || ek?.anaBaglantiAlani) touch(ek.anaAlan || ek.anaBaglantiAlani, "dimension", contractId);
      if (ek?.etiket) touch(ek.etiket, "measure", contractId);
      else if (ek?.koleksiyon && ek?.hedefAlan) touch(`${ek.koleksiyon}.${ek.hedefAlan}`, "measure", contractId);
      (ek?.filtreler || []).forEach(filter => {
        if (filter?.alan) touch(filter.alan, "dimension", contractId);
      });
      const catalog = SOURCE_FIELD_CATALOG[ek?.koleksiyon] || {};
      (catalog.dimensions || []).forEach(field => touch(field, "dimension", contractId));
    });
  });

  meta.forEach(value => {
    value.partial = selectedCount > 1 && value.contracts.size > 0 && !value.contracts.has("__base__") && value.contracts.size < selectedCount;
  });

  return meta;
}, [selectedSozlesmeler, calculatedFields, isMeasureField]);

const raporAlanlari = useMemo(() => {
  return Array.from(new Set([...raporAlanMeta.keys(), ...mevcutAlanlar, ...satirlar, ...sutunlar].map(canonicalFieldName)))
    .filter(field => field && !String(field).startsWith("_"))
    .filter(field => !raporAlanMeta.get(field)?.partial)
    .sort((a, b) => String(a).localeCompare(String(b), "tr", { numeric: true, sensitivity: "base" }));
}, [raporAlanMeta, mevcutAlanlar, satirlar, sutunlar]);

const isFieldUsableForRows = useCallback((fieldName) => {
  if (selectedSozlesmeler.length === 0) return true;
  return raporAlanMeta.has(fieldName) || calculatedFields.some(field => field.name === fieldName);
}, [selectedSozlesmeler.length, raporAlanMeta, calculatedFields]);

const isFieldUsableForMeasures = useCallback((fieldName) => {
  if (!isMeasureField(fieldName)) return false;
  if (selectedSozlesmeler.length === 0) return true;
  return raporAlanMeta.has(fieldName) || calculatedFields.some(field => field.name === fieldName);
}, [isMeasureField, selectedSozlesmeler.length, raporAlanMeta, calculatedFields]);

const getUnavailableSelectedFields = useCallback(() => {
  const rowFields = satirlar.filter(field => !isFieldUsableForRows(field));
  const measureFields = sutunlar.filter(field => !isFieldUsableForMeasures(field));
  return { rowFields, measureFields };
}, [satirlar, sutunlar, isFieldUsableForRows, isFieldUsableForMeasures]);

useEffect(() => {
  const handleOutsideClick = (e) => {
    // Eğer tıklanan yer filtre kutusunun dışındaysa kapat
    if (filterRef.current && !filterRef.current.contains(e.target)) {
      setOpenFilter(null);
      setFilterSearch("");
    }
  };
  document.addEventListener("mousedown", handleOutsideClick);
  return () => document.removeEventListener("mousedown", handleOutsideClick);
}, []);

const resolvePath = (obj, path) => {
  if (!path || !obj) return null;
  const parts = path.split('.');

  // 1. Koleksiyon adı temizleme sigortası (Eski kodundaki mantık)
  if (parts.length > 1 && obj[parts[0]] === undefined && obj[parts[1]] !== undefined) {
    parts.shift();
  }

  // 2. Dizi (Array) desteği (Personel görevleri için şart olan kısım)
  if (Array.isArray(obj[parts[0]])) {
    return obj[parts[0]].map(item => item[parts[1]]);
  }

  return parts.reduce((prev, curr) => (prev ? prev[curr] : null), obj);
};

const isPlateField = (fieldName = "") => {
  const f = String(fieldName).toLocaleLowerCase("tr-TR");
  return f.includes("plaka") || f.includes("plate");
};

const normalizePlateValue = (value) => {
  return String(value ?? "")
    .toLocaleUpperCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/İ/g, "I")
    .replace(/[^A-Z0-9]/g, "");
};

const normalizeComparableValue = (value) => {
  return String(value ?? "")
    .toLocaleUpperCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/İ/g, "I")
    .replace(/Ä°/g, "I")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ş/g, "S")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C")
    .replace(/[^A-Z0-9]/g, "");
};

const normalizeJoinValue = (value, fieldName = "") => {
  if (Array.isArray(value)) {
    return value.map(v => normalizeJoinValue(v, fieldName)).filter(Boolean).join("|");
  }
  if (isPlateField(fieldName)) return normalizePlateValue(value);
  return normalizeComparableValue(value);
};

const matchesFilter = (row, filter) => {
  if (!filter?.alan) return true;
  const fieldName = String(filter.alan).includes(".") ? String(filter.alan).split(".").pop() : filter.alan;
  const rowValue = resolvePath(row, fieldName);
  const rowNumber = parseFloat(rowValue);
  const filterNumber = parseFloat(filter.deger);

  if (!Number.isNaN(rowNumber) && !Number.isNaN(filterNumber)) {
    if (filter.op === ">") return rowNumber > filterNumber;
    if (filter.op === "<") return rowNumber < filterNumber;
    if (filter.op === ">=") return rowNumber >= filterNumber;
    if (filter.op === "<=") return rowNumber <= filterNumber;
    return rowNumber === filterNumber;
  }

  if (filter.op === "!=") return normalizeComparableValue(rowValue) !== normalizeComparableValue(filter.deger);
  if (["aracCinsi", "vehicleType", "kullanim", "tip"].includes(fieldName)) {
    const rowComparable = normalizeComparableValue(rowValue);
    const filterComparable = normalizeComparableValue(filter.deger);
    const vehicleTypeAliases = {
      TRANSMIKSER: ["TRANSMIKSER", "MIKSER"],
      MIKSER: ["MIKSER", "TRANSMIKSER"]
    };
    if (vehicleTypeAliases[filterComparable]?.includes(rowComparable)) return true;
  }
  return normalizeComparableValue(rowValue) === normalizeComparableValue(filter.deger);
};

const matchesFilters = (row, filters = []) => {
  return (filters || []).filter(f => f?.alan).every(filter => matchesFilter(row, filter));
};

const getSourcePlateFields = (anaKaynak = "") => {
  const source = String(anaKaynak).toLocaleLowerCase("tr-TR");
  if (source.includes("mikser")) return ["mikserPlaka", "vehiclePlate", "plaka"];
  if (source.includes("pompa")) return ["pompaPlaka", "vehiclePlate", "plaka"];
  return [];
};

const buildSourcePlateScope = (sourceRows = [], anaKaynak = "") => {
  const fields = getSourcePlateFields(anaKaynak);
  if (fields.length === 0) return { enabled: false, plates: new Set(), sourceFields: [] };

  const plates = new Set();
  sourceRows.forEach(row => {
    fields.forEach(field => {
      const key = normalizeJoinValue(resolvePath(row, field), field);
      if (key) plates.add(key);
    });
  });

  return { enabled: true, plates, sourceFields: fields };
};

const matchesSourcePlateScope = (row, sourcePlateScope) => {
  if (!sourcePlateScope?.enabled) return true;
  const plateKey = normalizeJoinValue(resolveFirstPath(row, FUEL_PLATE_FIELD_CANDIDATES), "vehiclePlate");
  return plateKey ? sourcePlateScope.plates.has(plateKey) : false;
};

const isPlateCardFilter = (filter) => String(filter?.alan || "").startsWith("plates.");
const stripPlateCardFilter = (filter) => ({
  ...filter,
  alan: String(filter?.alan || "").replace(/^plates\./, "")
});

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const normalized = value.includes(".")
      ? value.split(".").reverse().join("-")
      : value;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const isSamePeriod = (row, basDate, bitDate) => {
  const d = toJsDate(row.date || row.tarih || row.createdAt || row.updatedAt);
  if (!d) return true;
  return d >= basDate && d <= bitDate;
};

const toNumber = (value) => {
  if (typeof value === "number") return value;
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const formatDateValue = (value) => {
  const d = toJsDate(value);
  if (!d) return "";
  return d.toLocaleDateString("tr-TR");
};

const dateKeyValue = (value) => {
  const d = toJsDate(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") {
    const d = toJsDate(value);
    if (d) return formatDateValue(value);
    return JSON.stringify(value);
  }
  return value;
};

const groupValue = (value) => {
  const shown = displayValue(value);
  return shown === "" ? "Tanımsız" : shown;
};

const pivotGroupValue = useCallback((fieldName, value) => {
  if (isPlateField(fieldName)) {
    const plate = normalizePlateValue(value);
    return plate || "Tanımsız";
  }
  return groupValue(value);
}, []);

const resolveFirstPath = (obj, paths) => {
  for (const path of paths) {
    const value = resolvePath(obj, path);
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return "";
};

const getDayOfMonth = (row) => {
  const d = toJsDate(resolveFirstPath(row, DATE_FIELD_CANDIDATES));
  return d ? d.getDate() : "";
};

const getWeekOfMonth = (row) => {
  const day = getDayOfMonth(row);
  if (!day) return "";
  return day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5;
};

const isDayField = (field = "") => {
  const lower = String(field).toLocaleLowerCase("tr-TR");
  return lower === "gün" || lower === "gun" || lower === "gÃ¼n" || lower === "gã¼n" || lower === "gÃƒÂ¼n";
};

const isWeekField = (field = "") => String(field).toLocaleLowerCase("tr-TR") === "hafta";

const isDateField = (field = "") => {
  const lower = String(field).toLocaleLowerCase("tr-TR");
  return lower === "tarih" || lower === "date" || lower === "createdat" || lower === "updatedat";
};

const detailCanMatchPivotField = (field = "") => {
  const fieldLower = String(field).toLocaleLowerCase("tr-TR");
  return isDayField(field)
    || isWeekField(field)
    || isDateField(field)
    || fieldLower.includes("plaka")
    || fieldLower.includes("plate")
    || PERSON_FIELD_CANDIDATES.includes(field)
    || fieldLower.includes("personel")
    || fieldLower.includes("sofor")
    || fieldLower.includes("driver")
    || TESIS_FIELD_CANDIDATES.includes(field)
    || fieldLower.includes("tesis")
    || fieldLower.includes("receipt")
    || fieldLower.includes("fiş")
    || fieldLower.includes("fis")
    || fieldLower.includes("fatura");
};

const rowMatchesPivotDimension = (detailRow, sourceRow, field) => {
  const targetValue = sourceRow?.[field];
  if (targetValue === undefined || targetValue === null || targetValue === "") return true;
  if (isDayField(field)) return String(getDayOfMonth(detailRow)) === String(targetValue);
  if (isWeekField(field)) return String(getWeekOfMonth(detailRow)) === String(targetValue);
  if (isDateField(field)) {
    const detailDate = dateKeyValue(resolveFirstPath(detailRow, DATE_FIELD_CANDIDATES));
    const targetDate = dateKeyValue(targetValue);
    return detailDate && targetDate && detailDate === targetDate;
  }

  const fieldLower = String(field).toLocaleLowerCase("tr-TR");
  if (fieldLower.includes("plaka") || fieldLower.includes("plate")) {
    return normalizeJoinValue(resolveFirstPath(detailRow, FUEL_PLATE_FIELD_CANDIDATES), "vehiclePlate")
      === normalizeJoinValue(targetValue, field);
  }
  if (PERSON_FIELD_CANDIDATES.includes(field) || fieldLower.includes("personel") || fieldLower.includes("sofor") || fieldLower.includes("driver")) {
    const detailKeys = [
      normalizeJoinValue(detailRow.driverId, "driverId"),
      normalizeJoinValue(resolveFirstPath(detailRow, PERSON_FIELD_CANDIDATES), "driverName")
    ].filter(Boolean);
    return detailKeys.includes(normalizeJoinValue(targetValue, field));
  }
  if (TESIS_FIELD_CANDIDATES.includes(field) || fieldLower.includes("tesis")) {
    return normalizeJoinValue(resolveFirstPath(detailRow, TESIS_FIELD_CANDIDATES), "tesis")
      === normalizeJoinValue(targetValue, field);
  }
  if (fieldLower.includes("receipt") || fieldLower.includes("fiş") || fieldLower.includes("fis") || fieldLower.includes("fatura")) {
    return normalizeJoinValue(resolveFirstPath(detailRow, RECEIPT_FIELD_CANDIDATES), "receiptNo")
      === normalizeJoinValue(targetValue, field);
  }

  const detailValue = resolvePath(detailRow, field);
  if (detailValue === null || detailValue === undefined || detailValue === "") return !detailCanMatchPivotField(field);
  return normalizeJoinValue(detailValue, field) === normalizeJoinValue(targetValue, field);
};

const detailRowMatchesPivot = (detailRow, sourceRow, rowFields) => {
  return (rowFields || []).every(field => rowMatchesPivotDimension(detailRow, sourceRow, field));
};

const getDetailPivotValue = (detailRow, field) => {
  const fieldLower = String(field).toLocaleLowerCase("tr-TR");
  if (isDayField(field)) return getDayOfMonth(detailRow) || "Tanımsız";
  if (isWeekField(field)) return getWeekOfMonth(detailRow) || "Tanımsız";
  if (isDateField(field)) return groupValue(resolveFirstPath(detailRow, DATE_FIELD_CANDIDATES));
  if (fieldLower.includes("plaka") || fieldLower.includes("plate")) return pivotGroupValue(field, resolveFirstPath(detailRow, FUEL_PLATE_FIELD_CANDIDATES));
  if (PERSON_FIELD_CANDIDATES.includes(field) || fieldLower.includes("personel") || fieldLower.includes("sofor") || fieldLower.includes("driver")) {
    return displayValue(resolveFirstPath(detailRow, PERSON_FIELD_CANDIDATES)) || "Tanımsız";
  }
  if (TESIS_FIELD_CANDIDATES.includes(field) || fieldLower.includes("tesis")) {
    return displayValue(resolveFirstPath(detailRow, TESIS_FIELD_CANDIDATES)) || "Tanımsız";
  }
  if (fieldLower.includes("receipt") || fieldLower.includes("fiş") || fieldLower.includes("fis") || fieldLower.includes("fatura")) {
    return displayValue(resolveFirstPath(detailRow, RECEIPT_FIELD_CANDIDATES)) || "Tanımsız";
  }
  return groupValue(resolvePath(detailRow, field));
};

const canBuildPivotGroupFromDetail = (rowFields = []) => {
  return rowFields.length > 0 && rowFields.every(field => detailCanMatchPivotField(field));
};

const buildSourceActivityMap = (sourceRows = []) => {
  const map = new Map();
  sourceRows.forEach(row => {
    const plateKey = normalizeJoinValue(resolveFirstPath(row, PLATE_FIELD_CANDIDATES), "vehiclePlate");
    const dateKey = dateKeyValue(resolveFirstPath(row, DATE_FIELD_CANDIDATES));
    if (!plateKey || !dateKey) return;

    const rawPersons = [
      row.personelAdi,
      row.soforAdi,
      row.driverName,
      row.operatorAdi,
      row.yagciAdi,
      row.operator
    ].filter(Boolean);

    const current = map.get(`${plateKey}|${dateKey}`) || {
      personKeys: new Set(),
      personNames: [],
      tesis: displayValue(resolveFirstPath(row, TESIS_FIELD_CANDIDATES)) || ""
    };

    rawPersons.forEach(name => {
      const key = normalizeJoinValue(name, "driverName");
      if (!key || current.personKeys.has(key)) return;
      current.personKeys.add(key);
      current.personNames.push(displayValue(name));
    });

    if (!current.tesis) current.tesis = displayValue(resolveFirstPath(row, TESIS_FIELD_CANDIDATES)) || "";
    map.set(`${plateKey}|${dateKey}`, current);
  });

  return map;
};

const kmRowMatchesPivot = (detailRow, sourceRow, rowFields, scopeMode) => {
  return (rowFields || []).every(field => {
    const fieldLower = String(field).toLocaleLowerCase("tr-TR");
    const targetValue = sourceRow?.[field];
    if (targetValue === undefined || targetValue === null || targetValue === "") return true;

    if (scopeMode === "personel" && (PERSON_FIELD_CANDIDATES.includes(field) || fieldLower.includes("personel") || fieldLower.includes("sofor") || fieldLower.includes("driver"))) {
      const activityKeys = detailRow._activityPersonKeys || [];
      const fallbackKeys = [
        normalizeJoinValue(detailRow.driverId, "driverId"),
        normalizeJoinValue(resolveFirstPath(detailRow, PERSON_FIELD_CANDIDATES), "driverName")
      ].filter(Boolean);
      const keys = activityKeys.length > 0 ? activityKeys : fallbackKeys;
      return keys.includes(normalizeJoinValue(targetValue, field));
    }

    if (scopeMode === "personel" && (TESIS_FIELD_CANDIDATES.includes(field) || fieldLower.includes("tesis")) && detailRow._activityTesis) {
      return normalizeJoinValue(detailRow._activityTesis, "tesis") === normalizeJoinValue(targetValue, field);
    }

    return rowMatchesPivotDimension(detailRow, sourceRow, field);
  });
};

const getKmDetailPivotValue = (detailRow, field, scopeMode) => {
  const fieldLower = String(field).toLocaleLowerCase("tr-TR");
  if (scopeMode === "personel" && (PERSON_FIELD_CANDIDATES.includes(field) || fieldLower.includes("personel") || fieldLower.includes("sofor") || fieldLower.includes("driver"))) {
    return detailRow._activityPersonNames?.[0] || displayValue(resolveFirstPath(detailRow, PERSON_FIELD_CANDIDATES)) || "TanÄ±msÄ±z";
  }
  if (scopeMode === "personel" && (TESIS_FIELD_CANDIDATES.includes(field) || fieldLower.includes("tesis")) && detailRow._activityTesis) {
    return detailRow._activityTesis;
  }
  return getDetailPivotValue(detailRow, field);
};

const pickKmScope = useCallback((rowFields, scopeMode = "personel") => {
  const hasPerson = rowFields.some(field => PERSON_FIELD_CANDIDATES.includes(field));
  const hasPlate = rowFields.some(field => {
    const f = String(field).toLocaleLowerCase("tr-TR");
    return f.includes("plaka") || f.includes("plate") || PLATE_FIELD_CANDIDATES.includes(field);
  });
  const hasTesis = rowFields.some(field => TESIS_FIELD_CANDIDATES.includes(field));

  if (scopeMode === "plaka") return "plate";
  if (scopeMode === "tesis") return hasTesis ? "tesis" : "plate";
  if (hasPerson && hasPlate) return "driverPlate";
  if (hasPerson) return "driver";
  if (hasPlate) return "plate";
  if (hasTesis) return "tesis";
  return "plate";
}, []);

const tokenizeFormula = (formulaText) => {
  const tokens = [];
  let i = 0;
  const text = String(formulaText || "");

  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (ch === "[") {
      const end = text.indexOf("]", i + 1);
      if (end === -1) throw new Error("Alan kapatma parantezi eksik.");
      const field = text.slice(i + 1, end).trim();
      if (!field) throw new Error("Boş alan kullanılamaz.");
      tokens.push({ type: "field", value: field });
      i = end + 1;
      continue;
    }

    if ("+-*/()".includes(ch)) {
      tokens.push({ type: ch === "(" || ch === ")" ? "paren" : "operator", value: ch });
      i += 1;
      continue;
    }

    if (/[0-9.,]/.test(ch)) {
      let j = i;
      while (j < text.length && /[0-9.,]/.test(text[j])) j += 1;
      const raw = text.slice(i, j);
      const normalized = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;
      const value = Number(normalized);
      if (!Number.isFinite(value)) throw new Error(`Geçersiz sayı: ${raw}`);
      tokens.push({ type: "number", value });
      i = j;
      continue;
    }

    throw new Error(`Formülde desteklenmeyen karakter var: ${ch}`);
  }

  return tokens;
};

const calculateFormulaValue = (row, formulaText) => {
  const tokens = tokenizeFormula(formulaText);
  if (tokens.length === 0) return 0;

  const precedence = { "+": 1, "-": 1, "*": 2, "/": 2 };
  const output = [];
  const operators = [];

  tokens.forEach(token => {
    if (token.type === "number" || token.type === "field") {
      output.push(token);
      return;
    }

    if (token.type === "operator") {
      while (
        operators.length > 0 &&
        operators[operators.length - 1].type === "operator" &&
        precedence[operators[operators.length - 1].value] >= precedence[token.value]
      ) {
        output.push(operators.pop());
      }
      operators.push(token);
      return;
    }

    if (token.value === "(") {
      operators.push(token);
      return;
    }

    while (operators.length > 0 && operators[operators.length - 1].value !== "(") {
      output.push(operators.pop());
    }
    if (!operators.length) throw new Error("Parantez hatası.");
    operators.pop();
  });

  while (operators.length > 0) {
    const op = operators.pop();
    if (op.value === "(" || op.value === ")") throw new Error("Parantez hatası.");
    output.push(op);
  }

  const stack = [];
  output.forEach(token => {
    if (token.type === "number") {
      stack.push(token.value);
      return;
    }
    if (token.type === "field") {
      stack.push(toNumber(row[token.value]));
      return;
    }
    const b = stack.pop();
    const a = stack.pop();
    if (a === undefined || b === undefined) throw new Error("Formül eksik.");
    if (token.value === "+") stack.push(a + b);
    if (token.value === "-") stack.push(a - b);
    if (token.value === "*") stack.push(a * b);
    if (token.value === "/") stack.push(b === 0 ? 0 : a / b);
  });

  if (stack.length !== 1) throw new Error("Formül tamamlanamadı.");
  return Number.isFinite(stack[0]) ? stack[0] : 0;
};

// Tasarımları Yükle (reportDesigns koleksiyonundan)
useEffect(() => {
  const fetchDesigns = async () => {
    const q = query(collection(db, "reportDesigns"), where("module", "==", "hakedis_pivot"));
    const snap = await getDocs(q);
    setDesigns(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => a.name.localeCompare(b.name, "tr")));
  };
  fetchDesigns();
}, []);

// Dinamik Tarih Hesaplayıcı
const applyHakedisPreset = (p) => {
  setPreset(p);
  const bugun = new Date();
  const buAyStr = bugun.toISOString().slice(0, 7);
  
  if (p === "BU_AY") {
    setSeciliAylar([buAyStr]);
    setSeciliHaftalar([]);
  } else if (p === "GECEN_AY") {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    setSeciliAylar([d.toISOString().slice(0, 7)]);
    setSeciliHaftalar([]);
  } else if (p === "BU_HAFTA") {
    setSeciliAylar([buAyStr]);
    setSeciliHaftalar([Math.ceil(bugun.getDate() / 7).toString()]);
  } else if (p === "GECEN_HAFTA") {
    const gun = bugun.getDate();
    if (gun > 7) {
      setSeciliAylar([buAyStr]);
      setSeciliHaftalar([(Math.ceil(gun / 7) - 1).toString()]);
    } else {
      const d = new Date(); d.setMonth(d.getMonth() - 1);
      setSeciliAylar([d.toISOString().slice(0, 7)]);
      setSeciliHaftalar(["4", "5"]);
    }
  }
};

// Tasarımı Kaydet
const handleSaveDesign = async () => {
  if (!reportName.trim()) return alert("Lütfen tasarım adı girin!");
  
  // O anki kullanıcıyı al
  const auth = getAuth();
  const user = auth.currentUser;

  // Güvenlik kontrolü: Kullanıcı giriş yapmamışsa uyar
  if (!user) {
    return alert("Tasarım kaydetmek için oturum açmış olmanız gerekir!");
  }

  try {
    const payload = {
      name: reportName.trim(),
      module: "hakedis_pivot",
      satirlar: satirlar || [],
      sutunlar: sutunlar || [],
      seciliSozlesmeIds: seciliSozlesmeIds || [], 
      seciliAylar: seciliAylar || [],
      seciliHaftalar: seciliHaftalar || [],
      calculatedFields: calculatedFields || [],
      conditionalFormats: conditionalFormats || [],
      showSubtotals,
      reportScope,
      datePreset: preset || "OZEL",
      createdAt: serverTimestamp(),
      ownerEmail: user.email // Sabit mail yerine user.email kullanıy oruz
    };

    console.log("Kaydedilen Veri:", payload);

    const docRef = await addDoc(collection(db, "reportDesigns"), payload);
    
    setDesigns(prev => [...prev, { id: docRef.id, ...payload }].sort((a, b) => a.name.localeCompare(b.name, "tr")));
    setReportName("");
    alert("Tasarım başarıyla kaydedildi!");
  } catch (e) {
    console.error("Kaydetme Hatası:", e);
    alert("Kaydedilirken bir hata oluştu: " + e.message);
  }
};

// Tasarımı Uygula
const applyDesign = (id) => {
  setSelectedDesignId(id);
  const d = designs.find(x => x.id === id);
  if (!d) return;

  // 1. Pivot Dizilimini Uygula
  if (d.satirlar) setSatirlar(d.satirlar.map(canonicalFieldName));
  if (d.sutunlar) setSutunlar(d.sutunlar.map(canonicalFieldName));
  setCalculatedFields(d.calculatedFields || []);
  setConditionalFormats(d.conditionalFormats || []);
  setShowSubtotals(Boolean(d.showSubtotals));
  setReportScope(d.reportScope || "personel");
  setHamVeri([]);
  setDatasetInfo(null);

  // 2. Sözleşme Seçimini Uygula (String olduklarından emin oluyoruz)
  if (d.seciliSozlesmeIds) {
    const ids = d.seciliSozlesmeIds.map(val => String(val));
    setSeciliSozlesmeIds(ids);
  }

  // 3. Tarih Seçimleri
  // Eğer Preset varsa (Geçen Ay vb.) onu hesaplatıp set etsin
  if (d.datePreset && d.datePreset !== "OZEL") {
    applyHakedisPreset(d.datePreset);
  } else {
    // Özel seçim ise db'deki değerleri string'e zorlayarak bas
    if (d.seciliAylar) setSeciliAylar(d.seciliAylar.map(val => String(val)));
    if (d.seciliHaftalar) setSeciliHaftalar(d.seciliHaftalar.map(val => String(val)));
    setPreset("OZEL");
  }
};

const sonAylar = useMemo(() => {
  const aylar = [];
  const bugun = new Date();
  // Ayın 1'ine sabitleyelim ki 31. gün hataları oluşmasın
  const baslangic = new Date(bugun.getFullYear(), bugun.getMonth(), 1);

  for (let i = 0; i < 24; i++) { // Son 24 ayı getir (2025 ve 2024'ü kapsar)
    const d = new Date(baslangic);
    d.setMonth(baslangic.getMonth() - i);
    aylar.push(d.toISOString().slice(0, 7));
  }
  return aylar;
}, []);

  useEffect(() => { fetchSozlesmeler(); }, []);

  const fetchSozlesmeler = async () => {
    const snap = await getDocs(query(collection(db, "hakedis_tanimlari"), where("silindi", "==", false)));
    setSozlesmeler(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchOtomatikMazot = async (bas, bit) => {
    try {
      const q = query(collection(db, "fuel_deliveries"), where("date", ">=", bas), where("date", "<=", bit), orderBy("date", "desc"), limit(1));
      const s = await getDocs(q);
      if (!s.empty) return parseFloat(s.docs[0].data().unitPrice || 0);
    } catch (e) { console.error("Mazot çekilemedi:", e); }
    return 0;
  };

  const fetchEkBaglantiKayitlari = async (koleksiyon, bas, bit) => {
    const basDate = bas.toDate();
    const bitDate = bit.toDate();
    const dateFields = ["date", "tarih", "createdAt", "updatedAt"];

    for (const field of dateFields) {
      try {
        const snap = await getDocs(query(collection(db, koleksiyon), where(field, ">=", bas), where(field, "<=", bit)));
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.deleted !== true && r.silindi !== true);
        if (rows.length > 0) return rows;
      } catch (e) {
        console.warn(`${koleksiyon}.${field} tarih filtresi okunamadı`, e);
      }
    }

    const snap = await getDocs(query(collection(db, koleksiyon), limit(2000)));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => r.deleted !== true && r.silindi !== true && isSamePeriod(r, basDate, bitDate));
  };

  const buildEkBaglantiMaplari = async (sozlesme, bas, bit, sourcePlateScope = null) => {
    const baglantilar = (sozlesme.ekBaglantilar || []).filter(ek =>
      ek.koleksiyon && ek.baglantiAlani && ek.hedefAlan && (ek.anaAlan || ek.anaBaglantiAlani)
    );
    const maplar = [];

    for (const ek of baglantilar) {
      const rows = await fetchEkBaglantiKayitlari(ek.koleksiyon, bas, bit);
      const map = new Map();

      const directFilters = (ek.filtreler || []).filter(filter => !isPlateCardFilter(filter));
      rows
        .filter(row => matchesFilters(row, directFilters))
        .filter(row => ek.koleksiyon === "fuel_transactions" ? matchesSourcePlateScope(row, sourcePlateScope) : true)
        .forEach(row => {
        const key = normalizeJoinValue(resolvePath(row, ek.baglantiAlani), ek.baglantiAlani);
        if (!key) return;
        const current = map.get(key) || { sum: 0, count: 0, first: undefined, rows: [] };
        const rawValue = resolvePath(row, ek.hedefAlan);
        const numericValue = Number(rawValue) || 0;
        current.sum += numericValue;
        current.count += 1;
        if (current.first === undefined) current.first = rawValue;
        current.rows.push({ ...row, _ekDetailId: `${ek.koleksiyon}|${row.id || current.count}` });
        map.set(key, current);
      });

      maplar.push({
        ...ek,
        anaAlan: ek.anaAlan || ek.anaBaglantiAlani,
        etiket: ek.etiket || `${ek.koleksiyon}.${ek.hedefAlan}`,
        islem: ek.islem || "sum",
        map
      });
    }

    return maplar;
  };

  const getFuelKmFilters = (sozlesme) => {
    return (sozlesme.ekBaglantilar || [])
      .filter(ek => ek.koleksiyon === "fuel_transactions")
      .flatMap(ek => ek.filtreler || [])
      .filter(filter => !isPlateCardFilter(filter))
      .filter(f => f?.alan);
  };

  const buildVehicleTypePlateScope = async (sozlesme) => {
    const plateFilters = (sozlesme.ekBaglantilar || [])
      .filter(ek => ek.koleksiyon === "fuel_transactions")
      .flatMap(ek => ek.filtreler || [])
      .filter(isPlateCardFilter)
      .map(stripPlateCardFilter);
    if (plateFilters.length === 0) return { enabled: false, plates: new Set(), sourceFields: [] };

    try {
      const snap = await getDocs(collection(db, "plates"));
      const plates = new Set();
      snap.docs.forEach(docSnap => {
        const row = docSnap.data() || {};
        const plate = normalizeJoinValue(resolveFirstPath(row, ["plate", "plaka", "vehiclePlate"]), "vehiclePlate");
        if (!plate || !matchesFilters(row, plateFilters)) return;
        plates.add(plate);
      });
      return { enabled: true, plates, sourceFields: plateFilters.map(f => `plates.${f.alan}`) };
    } catch (e) {
      console.warn("Araç cinsi plaka kapsamı okunamadı", e);
      return { enabled: false, plates: new Set(), sourceFields: [] };
    }
  };

  const fetchFuelTransactionsForKm = async (bas, bit) => {
    // Önce tarih alanıyla Firestore sorgusu dene — tüm koleksiyonu çekme
    const dateFields = ["date", "tarih", "createdAt"];
    for (const field of dateFields) {
      try {
        const snap = await getDocs(
          query(
            collection(db, "fuel_transactions"),
            where(field, ">=", bas),
            where(field, "<=", bit),
            where("deleted", "==", false)
          )
        );
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => {
          const status = String(r.status || "").toLocaleLowerCase("tr-TR");
          const sourceType = String(r.sourceType || "").toLocaleLowerCase("tr-TR");
          return (!status || status === "approved") && (!sourceType || sourceType === "tank");
        });
        if (snap.docs.length > 0) return rows;
      } catch (e) {
        // Bu alan index yoksa veya başka hata — sonrakini dene
        console.warn(`fuel_transactions.${field} filtresi çalışmadı, sonraki deneniyor`, e.code);
      }
    }
    // Hiçbir tarih alanı çalışmadıysa sadece status filtresiyle çek + client-side tarih filtrele
    console.warn("fuel_transactions: Tarih indexi bulunamadı, tüm approved tank kayıtları çekiliyor");
    const basDate = bas.toDate();
    const bitDate = bit.toDate();
    const snap = await getDocs(query(collection(db, "fuel_transactions"), where("deleted", "==", false)));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => {
        const d2 = toJsDate(resolveFirstPath(r, DATE_FIELD_CANDIDATES));
        const status = String(r.status || "").toLocaleLowerCase("tr-TR");
        const sourceType = String(r.sourceType || "").toLocaleLowerCase("tr-TR");
        return r.deleted !== true
          && r.silindi !== true
          && (!status || status === "approved")
          && (!sourceType || sourceType === "tank")
          && d2 && d2 >= basDate && d2 <= bitDate;
      });
  };

  const buildEkBaglantiMaplariCached = async (sozlesme, bas, bit, sourcePlateScope, fetchEkKoleksiyon) => {
    const baglantilar = (sozlesme.ekBaglantilar || []).filter(ek =>
      ek.koleksiyon && ek.baglantiAlani && ek.hedefAlan && (ek.anaAlan || ek.anaBaglantiAlani)
    );
    const maplar = [];

    for (const ek of baglantilar) {
      // Cache'li fetch kullan
      const rows = await fetchEkKoleksiyon(ek.koleksiyon, bas, bit);
      const map = new Map();

      const directFilters = (ek.filtreler || []).filter(filter => !isPlateCardFilter(filter));
      const filteredRows = rows
        .filter(row => matchesFilters(row, directFilters))
        .filter(row => ek.koleksiyon === "fuel_transactions" ? matchesSourcePlateScope(row, sourcePlateScope) : true)
        .map((row, idx) => ({ ...row, _ekDetailId: `${ek.koleksiyon}|${row.id || idx}` }));
      filteredRows.forEach(row => {
          const key = normalizeJoinValue(resolvePath(row, ek.baglantiAlani), ek.baglantiAlani);
          if (!key) return;
          const current = map.get(key) || { sum: 0, count: 0, first: undefined, rows: [] };
          const rawValue = resolvePath(row, ek.hedefAlan);
          const numericValue = Number(rawValue) || 0;
          current.sum += numericValue;
          current.count += 1;
          if (current.first === undefined) current.first = rawValue;
          current.rows.push(row);
          map.set(key, current);
        });

      maplar.push({
        ...ek,
        anaAlan: ek.anaAlan || ek.anaBaglantiAlani,
        etiket: ek.etiket || `${ek.koleksiyon}.${ek.hedefAlan}`,
        islem: ek.islem || "sum",
        allRows: filteredRows,
        map
      });
    }
    return maplar;
  };

  // Pre-fetched fuelRows üzerinden KM hesapla — tekrar Firestore'a gitme
  const buildKmOzetleriFromRows = (allFuelRows, basDate, bitDate, filtreler = [], sourcePlateScope = null, sourceActivityMap = null) => {
    // Sadece bu ay aralığına ait kayıtları filtrele (globalBas-globalBit aralığında çekilmişti)
    const rows = allFuelRows.filter(r => {
      const d = toJsDate(resolveFirstPath(r, DATE_FIELD_CANDIDATES));
      return d && d <= bitDate; // basDate öncesi de lazım (KM farkı için)
    });

    const kmRows = buildFuelRowsWithDeltaKm(rows, {
      getId: (row) => row.id || row.receiptNo || row.fisNo || row.faturaNo,
      getPlate: (row) => normalizeJoinValue(resolveFirstPath(row, FUEL_PLATE_FIELD_CANDIDATES), "vehiclePlate"),
      getDateKey: (row) => dateKeyValue(resolveFirstPath(row, DATE_FIELD_CANDIDATES)),
      getOdometer: (row) => resolveFirstPath(row, ODOMETER_FIELD_CANDIDATES),
      getStoredDelta: (row) => resolveFirstPath(row, DELTA_KM_FIELD_CANDIDATES)
    }).map(row => ({
      ...row,
      _kmDate: toJsDate(resolveFirstPath(row, DATE_FIELD_CANDIDATES)),
      _odometerKm: toNumber(resolveFirstPath(row, ODOMETER_FIELD_CANDIDATES)),
      _plateKey: row._computedKmPlateKey,
      _kmSourceIndex: row._computedKmSourceIndex
    }));

    const maps = { plate: new Map(), driver: new Map(), driverPlate: new Map(), tesis: new Map(), records: [] };
    const addKm = (map, key, km) => { if (!key || !Number.isFinite(km) || km <= 0) return; map.set(key, (map.get(key) || 0) + km); };

    kmRows.forEach(curr => {
        if (!curr._kmDate || curr._kmDate < basDate || curr._kmDate > bitDate) return;
        if (!matchesFilters(curr, filtreler) || !matchesSourcePlateScope(curr, sourcePlateScope)) return;
        const km = Number(curr._computedKm || 0);
        if (km <= 0) return;
        const driverKeys = [
          normalizeJoinValue(curr.driverId, "driverId"),
          normalizeJoinValue(resolveFirstPath(curr, PERSON_FIELD_CANDIDATES), "driverName")
        ].filter(Boolean);
        const uniqueDriverKeys = [...new Set(driverKeys)];
        const tesisKey = normalizeJoinValue(resolveFirstPath(curr, TESIS_FIELD_CANDIDATES), "tesis");
        const activity = sourceActivityMap?.get(`${curr._plateKey}|${dateKeyValue(curr._kmDate)}`);
        addKm(maps.plate, curr._plateKey, km);

        maps.records.push({
          ...curr,
          _activityPersonKeys: activity ? Array.from(activity.personKeys || []) : [],
          _activityPersonNames: activity?.personNames || [],
          _activityTesis: activity?.tesis || "",
          _kmDetailId: curr._computedKmDetailId
        });
        uniqueDriverKeys.forEach(driverKey => {
          addKm(maps.driver, driverKey, km);
          addKm(maps.driverPlate, `${driverKey}|${curr._plateKey}`, km);
        });
        addKm(maps.tesis, tesisKey, km);
    });
    return maps;
  };

  const buildKmOzetleri = async (bas, bit, filtreler = [], sourcePlateScope = null) => {
    const basDate = bas.toDate();
    const bitDate = bit.toDate();
    const rows = await fetchFuelTransactionsForKm(bas, bit);
    return buildKmOzetleriFromRows(rows, basDate, bitDate, filtreler, sourcePlateScope);
  };

  const applyEkBaglantilar = (kayit, maplar) => {
    const ekAlanlar = {};
    const ekBaglantiKeys = {};
    const ekBaglantiRows = {};
    const ekBaglantiMeta = {};

    maplar.forEach(ek => {
      const key = normalizeJoinValue(resolvePath(kayit, ek.anaAlan), ek.anaAlan);
      const bulunan = key ? ek.map.get(key) : null;
      let value = 0;

      if (bulunan) {
        if (ek.islem === "count") value = bulunan.count;
        else if (ek.islem === "first") value = bulunan.first ?? "";
        else value = bulunan.sum;
      }

      ekAlanlar[ek.etiket] = value;
      ekBaglantiKeys[ek.etiket] = `${ek.etiket}|${key || "__empty__"}`;
      ekBaglantiRows[ek.etiket] = bulunan?.rows || [];
      ekBaglantiMeta[ek.etiket] = { hedefAlan: ek.hedefAlan, islem: ek.islem || "sum", allRows: ek.allRows || [] };
    });

    return { ekAlanlar, ekBaglantiKeys, ekBaglantiRows, ekBaglantiMeta };
  };

  const applyKmOzetleri = (kayit, kmOzetleri, scopeMode = "personel") => {
    const plateKey = normalizeJoinValue(resolveFirstPath(kayit, PLATE_FIELD_CANDIDATES), "vehiclePlate");
    const driverRaw = kayit.personelId || kayit.driverId || resolveFirstPath(kayit, PERSON_FIELD_CANDIDATES);
    const driverKey = normalizeJoinValue(driverRaw, "driverName");
    const tesisKey = normalizeJoinValue(resolveFirstPath(kayit, TESIS_FIELD_CANDIDATES), "tesis");
    const driverPlateKey = driverKey && plateKey ? `${driverKey}|${plateKey}` : "";

    return {
      "Toplam KM": 0,
      _kmRows: kmOzetleri.records.filter(row => {
        const rowPlate = normalizeJoinValue(resolveFirstPath(row, FUEL_PLATE_FIELD_CANDIDATES), "vehiclePlate");
        const rowDriverKeys = [
          normalizeJoinValue(row.driverId, "driverId"),
          normalizeJoinValue(resolveFirstPath(row, PERSON_FIELD_CANDIDATES), "driverName")
        ].filter(Boolean);
        if (plateKey && rowPlate !== plateKey) return false;
        if (scopeMode === "fuelDriver" && driverKey && !rowDriverKeys.includes(driverKey)) return false;
        return true;
      }),
      _kmKeys: {
        plate: plateKey ? `plate|${plateKey}` : "",
        driver: driverKey ? `driver|${driverKey}` : "",
        driverPlate: driverPlateKey ? `driverPlate|${driverPlateKey}` : "",
        tesis: tesisKey ? `tesis|${tesisKey}` : ""
      },
      _kmValues: {
        plate: plateKey ? (kmOzetleri.plate.get(plateKey) || 0) : 0,
        driver: driverKey ? (kmOzetleri.driver.get(driverKey) || 0) : 0,
        driverPlate: driverPlateKey ? (kmOzetleri.driverPlate.get(driverPlateKey) || 0) : 0,
        tesis: tesisKey ? (kmOzetleri.tesis.get(tesisKey) || 0) : 0
      }
    };
  };

const veriGetir = async () => {
    if (seciliSozlesmeIds.length === 0) return alert("Sözleşme seçin!");
    const unavailable = getUnavailableSelectedFields();
    if (unavailable.rowFields.length > 0 || unavailable.measureFields.length > 0) {
      const lines = [];
      if (unavailable.rowFields.length > 0) lines.push(`Secili sozlesmelerde bulunmayan satir alanlari: ${unavailable.rowFields.map(getGörünenAd).join(", ")}`);
      if (unavailable.measureFields.length > 0) lines.push(`Hesaplanabilir deger olmayan sutunlar: ${unavailable.measureFields.map(getGörünenAd).join(", ")}`);
      return alert(lines.join("`n"));
    }
    setLoading(true);
    setHamVeri([]); 
    setDatasetInfo(null);
    let sonuclar = [];
    const alanSeti = new Set(["Gün", "Hafta", "Tesis", "m3", "fiiliM3", "votoKm", "Toplam KM", "Sefer Sayısı", "Hakediş", "personelAdi", "personelGorevi"]);

    try {
      // ---- Tüm aylar için tarih aralığını tek seferde hesapla ----
      const tumAylar = seciliAylar;
      const enKucukBas = new Date(Math.min(...tumAylar.map(ay => {
        const [y, m] = ay.split("-").map(Number); return new Date(y, m - 1, 1).getTime();
      })));
      const enBuyukBit = new Date(Math.max(...tumAylar.map(ay => {
        const [y, m] = ay.split("-").map(Number); return new Date(y, m, 0, 23, 59, 59).getTime();
      })));
      const fuelBasDate = new Date(enKucukBas);
      fuelBasDate.setDate(fuelBasDate.getDate() - 45);
      const fuelGlobalBas = Timestamp.fromDate(fuelBasDate);
      const globalBit = Timestamp.fromDate(enBuyukBit);

      // ---- plates koleksiyonunu bir kez çek (tüm sözleşmeler için ortak) ----
      const platesSnap = await getDocs(collection(db, "plates"));
      const platesRows = platesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // ---- fuel_transactions'ı bir kez çek (tüm sözleşmeler/aylar için ortak) ----
      const fuelRows = await fetchFuelTransactionsForKm(fuelGlobalBas, globalBit);

      // ---- Ek bağlantı koleksiyonlarını bir kez çek (koleksiyon bazında cache) ----
      const ekKoleksiyonCache = new Map(); // koleksiyon -> rows

      const fetchEkKoleksiyon = async (koleksiyon, bas, bit) => {
        const cacheKey = `${koleksiyon}|${bas.seconds}|${bit.seconds}`;
        if (ekKoleksiyonCache.has(cacheKey)) return ekKoleksiyonCache.get(cacheKey);
        const rows = await fetchEkBaglantiKayitlari(koleksiyon, bas, bit);
        ekKoleksiyonCache.set(cacheKey, rows);
        return rows;
      };

      for (const sId of seciliSozlesmeIds) {
        const sozlesme = sozlesmeler.find(s => s.id === sId);
        if (!sozlesme) continue;
        alanSeti.add(getHakedisSozlesmeKolonu(sozlesme.baslik));

        // plates filtresini sözleşme bazında bir kez hesapla
        const plateFilters = (sozlesme.ekBaglantilar || [])
          .filter(ek => ek.koleksiyon === "fuel_transactions")
          .flatMap(ek => ek.filtreler || [])
          .filter(isPlateCardFilter)
          .map(stripPlateCardFilter);

        let vehicleTypePlateScope = { enabled: false, plates: new Set(), sourceFields: [] };
        if (plateFilters.length > 0) {
          const plates = new Set();
          platesRows.forEach(row => {
            const plate = normalizeJoinValue(resolveFirstPath(row, ["plate", "plaka", "vehiclePlate"]), "vehiclePlate");
            if (!plate || !matchesFilters(row, plateFilters)) return;
            plates.add(plate);
          });
          vehicleTypePlateScope = { enabled: true, plates, sourceFields: plateFilters.map(f => `plates.${f.alan}`) };
        }

        for (const ay of seciliAylar) {
          const [y, m] = ay.split("-").map(Number);
          const basDate = new Date(y, m - 1, 1);
          const bitDate = new Date(y, m, 0, 23, 59, 59);
          const bas = Timestamp.fromDate(basDate);
          const bit = Timestamp.fromDate(bitDate);
          const cekilenMazot = await fetchOtomatikMazot(bas, bit);

          const anaKaynak = sozlesme.kurallar?.[0]?.anaKaynak || "hazirbeton_pompa_hakedis";
          const querySnapshot = await getDocs(query(collection(db, anaKaynak), where("tarih", ">=", bas), where("tarih", "<=", bit)));
          const sourceRows = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          const sourcePlateScope = buildSourcePlateScope(sourceRows, anaKaynak);
          const sourceActivityMap = buildSourceActivityMap(sourceRows);
          const fuelPlateScope = vehicleTypePlateScope.enabled ? vehicleTypePlateScope : sourcePlateScope;

          // Ek bağlantıları cache kullanarak doldur
          const ekBaglantiMaplari = await buildEkBaglantiMaplariCached(sozlesme, bas, bit, fuelPlateScope, fetchEkKoleksiyon);

          // KM özetlerini global fuelRows üzerinden hesapla (yeniden çekme)
          const kmOzetleri = buildKmOzetleriFromRows(fuelRows, basDate, bitDate, getFuelKmFilters(sozlesme), fuelPlateScope, sourceActivityMap);
          
          for (const docSnap of querySnapshot.docs) {
            const d = docSnap.data();
            const tarih = d.tarih?.toDate() || new Date();
            const gunDegeri = tarih.getDate();
            const haftaNoDegeri = gunDegeri <= 7 ? 1 : gunDegeri <= 14 ? 2 : gunDegeri <= 21 ? 3 : gunDegeri <= 28 ? 4 : 5;

            // Personelleri belirle (Eğer yoksa boş dizi döner)
            const personeller = [];
            if (d.operatorAdi) personeller.push({ adSoyad: d.operatorAdi, gorev: "POMPA OPERATÖRÜ" });
            if (d.yagciAdi) personeller.push({ adSoyad: d.yagciAdi, gorev: "POMPA OPERATÖR YARDIMCISI" });

            // KRİTİK NOKTA: Eğer personel hakedişi değilse (Motorin vb.), döngünün dönmesi için sahte bir eleman ekliyoruz
            const islemListesi = personeller.length > 0 ? personeller : [null];

            for (const p of islemListesi) {
              for (const kural of (sozlesme.kurallar || [])) {
                // Test objesini oluştur (Eğer p varsa personel verisi eklenir)
                const testObjesi = { ...d, personeller: p ? [p] : [] };

                if (seciliHaftalar.length > 0 && !seciliHaftalar.includes(haftaNoDegeri.toString())) continue;

// YERİNE BUNU YAPIŞTIR
let kuralUygun = true;
if (kural.filtreler && kural.filtreler.length > 0) {
  kuralUygun = kural.filtreler.every(f => {
    // 1. Alan adını temizle (hazirbeton_mikser_sevkiyat.votoKm -> votoKm)
    const temizAlan = f.alan.includes('.') ? f.alan.split('.').pop() : f.alan;
    
    // 2. Değerleri al (Verideki 15, kuraldaki 10 gibi)
    const hamVeriDegeri = resolvePath(testObjesi, temizAlan);
    const veriSayi = parseFloat(hamVeriDegeri);
    const kuralSayi = parseFloat(f.deger);

    // 3. Eğer her iki taraf da sayıysa, operatöre göre (>, <, >=, <=) kıyasla
    if (!isNaN(veriSayi) && !isNaN(kuralSayi)) {
      if (f.op === ">") return veriSayi > kuralSayi;
      if (f.op === "<") return veriSayi < kuralSayi;
      if (f.op === ">=") return veriSayi >= kuralSayi;
      if (f.op === "<=") return veriSayi <= kuralSayi;
      if (f.op === "==") return veriSayi === kuralSayi;
      // Operatör yoksa veya bilinmiyorsa tam eşitliğe bak
      return veriSayi === kuralSayi;
    }

    // 4. Eğer sayı değilse (Plaka veya Tesis adı gibi metinse) tam eşleşmeye bak
    return normalizeComparableValue(hamVeriDegeri) === normalizeComparableValue(f.deger);
  });
}

                if (kuralUygun) {
                  const temelKayit = { ...d, personelAdi: p ? p.adSoyad : (d.soforAdi || "") };
                  const { ekAlanlar, ekBaglantiKeys, ekBaglantiRows, ekBaglantiMeta } = applyEkBaglantilar(temelKayit, ekBaglantiMaplari);
                  const kmAlanlari = applyKmOzetleri(temelKayit, kmOzetleri, reportScope);
                  const yeniKayit = {
                    ...d,
                    ...ekAlanlar,
                    ...kmAlanlari,
                    personelAdi: p ? p.adSoyad : (d.soforAdi || ""),
                    personelGorevi: p ? p.gorev : "ŞOFÖR/ARAÇ",
                    Gün: gunDegeri,
                    Hafta: haftaNoDegeri,
                    Tesis: d.Tesis_Adı || d.tesis || "Merkez",
                    m3: parseFloat(d.m3 || d.fiiliM3 || 0),
                    votoKm: parseFloat(d.votoKm || 0),
                    "Sefer Sayısı": 1,
                    _katsayi: parseFloat(kural.katsayi || 0),
                    _mod: kural.mod,
                    _carpanAlan: kural.carpanAlan,
                    _birim: kural.birim,
                    _seferKey: String(resolvePath(d, kural.carpanAlan) || docSnap.id),
                    _sourceDocKey: `${anaKaynak}|${docSnap.id}`,
                    _ekBaglantiKeys: ekBaglantiKeys,
                    _ekBaglantiRows: ekBaglantiRows,
                    _ekBaglantiMeta: ekBaglantiMeta,
                    _otoMazot: cekilenMazot,
                    sozlesmeAdi: sozlesme.baslik,
                    _hakedisKolonu: getHakedisSozlesmeKolonu(sozlesme.baslik)
                  };

                  sonuclar.push(yeniKayit);
                  Object.keys(yeniKayit).forEach(k => {
                    if(typeof yeniKayit[k] !== 'object' && k !== 'tarih') alanSeti.add(k);
                  });
                }
              }
            }
          }
        }
      }
      calculatedFields.forEach(field => {
        if (field?.name) alanSeti.add(field.name);
      });
      setMevcutAlanlar(Array.from(alanSeti));
      setHamVeri(sonuclar);
      setDatasetInfo({
        rowCount: sonuclar.length,
        fieldCount: alanSeti.size,
        calculatedAt: new Date()
      });
    } catch (error) { console.error("Hata:", error); } 
    finally { setLoading(false); }
};

  const mazotGerekliMi = useMemo(() => {
    return seciliSozlesmeIds.some(id => {
      const s = sozlesmeler.find(x => x.id === id);
      return s?.kurallar?.some(k => k.mod === "mazot");
    });
  }, [seciliSozlesmeIds, sozlesmeler]);

// --- ADIM 1: VERİYİ SÜZGEÇTEN GEÇİR ---
  const filteredHamVeri = useMemo(() => {
    return hamVeri.filter((item) => {
      return Object.entries(filters).every(([key, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        return selectedValues.includes(String(pivotGroupValue(key, item[key])));
      });
    });
  }, [hamVeri, filters]);

  // --- ADIM 2: SÜZÜLMÜŞ VERİYLE TABLOYU HESAPLA ---
const basePivotData = useMemo(() => {
    if (filteredHamVeri.length === 0) return [];

    // 1. Gruplama İşlemi
    const gruplar = filteredHamVeri.reduce((acc, curr) => {
      const key = satirlar.map(s => pivotGroupValue(s, curr[s])).join(" | ");
      if (!acc[key]) {
        acc[key] = { 
          id: key, 
          ...satirlar.reduce((o, s) => ({ ...o, [s]: pivotGroupValue(s, curr[s]) }), {}), 
          _rawItems: [],
          _seferKeys: new Set(),
          _sourceMetricKeys: {},
          _ekBaglantiKeys: {},
          _ekGlobalProcessed: {},
          _kmKeys: new Set(),
          _kmDetailMode: false
        };
        sutunlar.forEach(s => { if (s !== "Hakediş" && !s.startsWith(HAKEDIS_SOZLESME_PREFIX)) acc[key][s] = 0; });
      }
      sutunlar.forEach(s => {
        if (!isMeasureField(s)) return;
        if (s !== "Hakediş" && !s.startsWith(HAKEDIS_SOZLESME_PREFIX)) {
          if (s === "Sefer Sayısı") {
            const seferKey = curr._sourceDocKey || curr._seferKey || curr.id || `${key}-${acc[key]._rawItems.length}`;
            if (!acc[key]._seferKeys.has(seferKey)) {
              acc[key]._seferKeys.add(seferKey);
              acc[key][s] += 1;
            }
            return;
          }
          if (curr._ekBaglantiKeys?.[s]) {
            if (!acc[key]._ekBaglantiKeys[s]) acc[key]._ekBaglantiKeys[s] = new Set();
            const detailRows = curr._ekBaglantiRows?.[s] || [];
            const meta = curr._ekBaglantiMeta?.[s] || {};
            const canUseGlobalRows = (meta.allRows || []).length > 0 && satirlar.every(field => detailCanMatchPivotField(field));
            const candidateRows = canUseGlobalRows ? meta.allRows : detailRows;
            const matchSource = canUseGlobalRows ? acc[key] : curr;

            if (canUseGlobalRows && acc[key]._ekGlobalProcessed[s]) return;

            const matchedRows = candidateRows.filter(detailRow => detailRowMatchesPivot(detailRow, matchSource, satirlar));
            if (canUseGlobalRows) acc[key]._ekGlobalProcessed[s] = true;

            if (matchedRows.length > 0) {
              matchedRows.forEach(detailRow => {
                const ekKey = `${s}|${detailRow._ekDetailId || detailRow.id || acc[key]._ekBaglantiKeys[s].size}`;
                if (acc[key]._ekBaglantiKeys[s].has(ekKey)) return;
                acc[key]._ekBaglantiKeys[s].add(ekKey);
                const rawValue = resolvePath(detailRow, meta.hedefAlan);
                acc[key][s] += meta.islem === "count" ? 1 : toNumber(rawValue);
              });
              return;
            }

            if (satirlar.some(field => isDayField(field) || isWeekField(field) || isDateField(field))) return;

            const ekKey = curr._ekBaglantiKeys[s];
            if (!acc[key]._ekBaglantiKeys[s].has(ekKey)) {
              acc[key]._ekBaglantiKeys[s].add(ekKey);
              acc[key][s] += parseFloat(curr[s] || 0);
            }
            return;
          }
          if (s === "Toplam KM") {
            const kmRows = (curr._kmRows || []).filter(detailRow => kmRowMatchesPivot(detailRow, curr, satirlar, reportScope));
            if (kmRows.length > 0) {
              acc[key]._kmDetailMode = true;
              kmRows.forEach(detailRow => {
                const kmKey = detailRow._kmDetailId || detailRow.id;
                if (!kmKey || acc[key]._kmKeys.has(kmKey)) return;
                acc[key]._kmKeys.add(kmKey);
                acc[key][s] += Number(detailRow._computedKm || 0);
              });
              return;
            }

            if (acc[key]._kmDetailMode) return;

            if (satirlar.some(field => isDayField(field) || isWeekField(field) || isDateField(field))) return;

            const scope = pickKmScope(satirlar, reportScope);
            let kmKey = curr._kmKeys?.[scope];
            let kmValue = Number(curr._kmValues?.[scope] || 0);

            if (scope === "tesis" && (!kmKey || kmValue === 0)) {
              kmKey = curr._kmKeys?.plate;
              kmValue = Number(curr._kmValues?.plate || 0);
            }

            if (kmKey && !acc[key]._kmKeys.has(kmKey)) {
              acc[key]._kmKeys.add(kmKey);
              acc[key][s] += kmValue;
            }
            return;
          }
          if (SOURCE_DEDUP_FIELDS.has(s)) {
            if (!acc[key]._sourceMetricKeys[s]) acc[key]._sourceMetricKeys[s] = new Set();
            const metricKey = `${s}|${curr._sourceDocKey || curr.id || curr._seferKey || acc[key]._rawItems.length}`;
            if (!acc[key]._sourceMetricKeys[s].has(metricKey)) {
              acc[key]._sourceMetricKeys[s].add(metricKey);
              acc[key][s] += parseFloat(curr[s] || 0);
            }
            return;
          }
          const val = parseFloat(curr[s] || 0);
          acc[key][s] += val;
        }
      });
      acc[key]._rawItems.push(curr);
      return acc;
    }, {});

    // 2. Hakediş ve Mazot Hesaplama
    const ekMetaByField = new Map();
    filteredHamVeri.forEach(row => {
      Object.entries(row._ekBaglantiMeta || {}).forEach(([field, meta]) => {
        if (!sutunlar.includes(field) || !isMeasureField(field)) return;
        if (!meta?.allRows?.length || ekMetaByField.has(field)) return;
        ekMetaByField.set(field, meta);
      });
    });

    const createEmptyGroupFromDetail = (detailRow, valueGetter = getDetailPivotValue) => {
      const key = satirlar.map(s => valueGetter(detailRow, s)).join(" | ");
      if (!gruplar[key]) {
        gruplar[key] = {
          id: key,
          ...satirlar.reduce((o, s) => ({ ...o, [s]: valueGetter(detailRow, s) }), {}),
          _rawItems: [],
          _seferKeys: new Set(),
          _sourceMetricKeys: {},
          _ekBaglantiKeys: {},
          _ekGlobalProcessed: {},
          _kmKeys: new Set(),
          _kmDetailMode: false,
          _detailOnly: true
        };
        sutunlar.forEach(s => {
          if (isMeasureField(s) && s !== "Hakediş" && s !== "HakediÅŸ" && !s.startsWith(HAKEDIS_SOZLESME_PREFIX)) {
            gruplar[key][s] = 0;
          }
        });
      }
      return gruplar[key];
    };

    const detailMatchesActiveFilters = (detailRow, valueGetter = getDetailPivotValue) => {
      return Object.entries(filters || {}).every(([field, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        if (!detailCanMatchPivotField(field)) return false;
        const value = String(pivotGroupValue(field, valueGetter(detailRow, field)));
        return selectedValues.includes(value);
      });
    };

    if (canBuildPivotGroupFromDetail(satirlar) && ekMetaByField.size > 0) {
      ekMetaByField.forEach((meta, field) => {
        (meta.allRows || []).forEach((detailRow, index) => {
          if (!detailMatchesActiveFilters(detailRow)) return;
          const group = createEmptyGroupFromDetail(detailRow);
          if (!group._ekBaglantiKeys[field]) group._ekBaglantiKeys[field] = new Set();
          const ekKey = `${field}|${detailRow._ekDetailId || detailRow.id || index}`;
          if (group._ekBaglantiKeys[field].has(ekKey)) return;
          group._ekBaglantiKeys[field].add(ekKey);
          const rawValue = resolvePath(detailRow, meta.hedefAlan);
          group[field] = (group[field] || 0) + (meta.islem === "count" ? 1 : toNumber(rawValue));
        });
      });
    }

    if (sutunlar.includes("Toplam KM") && canBuildPivotGroupFromDetail(satirlar)) {
      const kmRowsByKey = new Map();
      filteredHamVeri.forEach(row => {
        (row._kmRows || []).forEach((detailRow, index) => {
          const kmKey = detailRow._kmDetailId || detailRow.id || `${resolveFirstPath(detailRow, FUEL_PLATE_FIELD_CANDIDATES)}|${dateKeyValue(resolveFirstPath(detailRow, DATE_FIELD_CANDIDATES))}|${index}`;
          if (!kmRowsByKey.has(kmKey)) kmRowsByKey.set(kmKey, detailRow);
        });
      });

      kmRowsByKey.forEach((detailRow, kmKey) => {
        if (!detailMatchesActiveFilters(detailRow, (row, field) => getKmDetailPivotValue(row, field, reportScope))) return;
        const group = createEmptyGroupFromDetail(detailRow, (row, field) => getKmDetailPivotValue(row, field, reportScope));
        group._kmDetailMode = true;
        if (group._kmKeys.has(kmKey)) return;
        group._kmKeys.add(kmKey);
        group["Toplam KM"] = (group["Toplam KM"] || 0) + Number(detailRow._computedKm || 0);
      });
    }

    let result = Object.values(gruplar).map(g => {
      const varsayilanMazot = g._rawItems[0]?._otoMazot || 0;
      const mazotFiyati = manuelMazotlar[g.id] !== undefined ? manuelMazotlar[g.id] : varsayilanMazot;
      let toplamHakedis = 0;
      const hakedisBySozlesme = {};
      const hesaplananSeferler = new Set();
      g._rawItems.forEach(item => {
        if (item._birim === "sefer") {
          const seferKey = `${item.sozlesmeAdi}|${item._katsayi}|${item._mod}|${item._seferKey}`;
          if (hesaplananSeferler.has(seferKey)) return;
          hesaplananSeferler.add(seferKey);
        }
        const carpan = item._birim === "sefer" ? 1 : parseFloat(item[item._carpanAlan] || 0);
        const hakedisTutari = (item._mod === "mazot") ? (carpan * mazotFiyati * item._katsayi) : (carpan * item._katsayi);
        const hakedisKolonu = item._hakedisKolonu || getHakedisSozlesmeKolonu(item.sozlesmeAdi);
        toplamHakedis += hakedisTutari;
        hakedisBySozlesme[hakedisKolonu] = (hakedisBySozlesme[hakedisKolonu] || 0) + hakedisTutari;
      });
      return { ...g, ...hakedisBySozlesme, Hakediş: toplamHakedis, Mazot: mazotFiyati };
    });

    result = result.map(row => {
      const calcValues = {};
      calculatedFields.forEach(field => {
        if (!field?.name || !field?.formulaText) return;
        try {
          calcValues[field.name] = calculateFormulaValue(row, field.formulaText);
        } catch (e) {
          calcValues[field.name] = 0;
        }
      });
      return { ...row, ...calcValues };
    });

    // 3. SIRALAMA (Burayı yeni ekledik)
// pivotData useMemo içindeki sıralama kısmı:
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredHamVeri, satirlar, sutunlar, manuelMazotlar, pickKmScope, calculatedFields, isMeasureField, reportScope, filters]);

const pivotData = useMemo(() => {
    const createSubtotalRow = (rows, level, prefixValues) => {
      const subtotal = {
        id: `subtotal|${level}|${prefixValues.join("|")}`,
        _isSubtotal: true,
        _subtotalLevel: level
      };
      satirlar.forEach((field, idx) => {
        if (idx <= level) subtotal[field] = prefixValues[idx] || "";
        else if (idx === level + 1) subtotal[field] = "Ara Toplam";
        else subtotal[field] = "";
      });
      sutunlar.forEach(field => {
        subtotal[field] = isMeasureField(field)
          ? rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0)
          : "";
      });
      return subtotal;
    };

    const buildSubtotalRows = (rows, level = 0, prefixValues = []) => {
      if (level >= satirlar.length - 1) return rows;
      const field = satirlar[level];
      const groups = new Map();
      rows.forEach(row => {
        const value = row[field] || "Tanımsız";
        if (!groups.has(value)) groups.set(value, []);
        groups.get(value).push(row);
      });
      const out = [];
      Array.from(groups.entries())
        .sort(([a], [b]) => String(a).localeCompare(String(b), "tr", { numeric: true, sensitivity: "base" }))
        .forEach(([value, groupRows]) => {
          const nextPrefix = [...prefixValues, value];
          out.push(...buildSubtotalRows(groupRows, level + 1, nextPrefix));
          out.push(createSubtotalRow(groupRows, level, nextPrefix));
        });
      return out;
    };

    let result = showSubtotals && !sortedBy.col && satirlar.length > 1
      ? buildSubtotalRows(basePivotData)
      : [...basePivotData];

    if (sortedBy.col) {
      result.sort((a, b) => {
        let vA = a[sortedBy.col];
        let vB = b[sortedBy.col];
        if (sortedBy.asc) return String(vA).localeCompare(String(vB), "tr", { numeric: true });
        return String(vB).localeCompare(String(vA), "tr", { numeric: true });
      });
    }

    return result;
  }, [basePivotData, showSubtotals, sortedBy, satirlar, sutunlar, isMeasureField]);

  const handleMazotChange = (index, value) => {
    const fiyat = value === "" ? 0 : parseFloat(value);
    const yeniMazotlar = { ...manuelMazotlar };
    for (let i = index; i < pivotData.length; i++) {
      yeniMazotlar[pivotData[i].id] = fiyat;
    }
    setManuelMazotlar(yeniMazotlar);
  };

  const getGörünenAd = (id) => {
    const field = canonicalFieldName(id);
    return alanIsimleri[field] || getFieldLabel(field);
  };

  const addMeasureField = (fieldName) => {
    const field = canonicalFieldName(fieldName);
    if (!isFieldUsableForMeasures(field)) {
      alert(`${getGörünenAd(field)} hesaplanabilir bir deger alani degil. Bu alani satir/grup tarafinda kullanabilirsiniz.`);
      return;
    }
    setSutunlar(prev => prev.includes(field) ? prev : [...prev, field]);
  };

  const hesapAlanlari = useMemo(() => {
    return Array.from(new Set([...raporAlanlari, ...mevcutAlanlar, ...sutunlar, ...satirlar]))
      .filter(a => a && !String(a).startsWith("_"))
      .filter(a => isFieldUsableForMeasures(a))
      .sort((a, b) => String(a).localeCompare(String(b), "tr", { numeric: true }));
  }, [raporAlanlari, mevcutAlanlar, sutunlar, satirlar, isFieldUsableForMeasures]);

  const handleAddCalculatedField = () => {
    const name = calcDraft.name.trim();
    const formulaText = calcDraft.formulaText.trim();
    if (!name) return alert("Hesap alanı adı girin.");
    if (!formulaText) return alert("Formül girin.");
    if (mevcutAlanlar.includes(name) || calculatedFields.some(f => f.name === name)) {
      return alert("Bu isimde bir alan zaten var.");
    }

    try {
      calculateFormulaValue({}, formulaText);
    } catch (e) {
      return alert("Formül hatalı: " + e.message);
    }

    const usedFields = tokenizeFormula(formulaText)
      .filter(token => token.type === "field")
      .map(token => token.value);
    const invalidFields = usedFields.filter(fieldName => !isFieldUsableForMeasures(fieldName));
    if (invalidFields.length > 0) {
      return alert(`Formulde sadece hesaplanabilir sayisal deger alanlari kullanilabilir. Uygun olmayan alanlar: ${invalidFields.map(getGörünenAd).join(", ")}`);
    }

    const field = {
      id: makeCalcId(),
      name,
      formulaText,
      format: calcDraft.format || "number",
      decimals: Number(calcDraft.decimals) || 0
    };

    setCalculatedFields(prev => [...prev, field]);
    setMevcutAlanlar(prev => Array.from(new Set([...prev, name])));
    setCalcDraft({ name: "", formulaText: "", format: "number", decimals: 2 });
  };

  const handleRemoveCalculatedField = (fieldName) => {
    setCalculatedFields(prev => prev.filter(f => f.name !== fieldName));
    setSutunlar(prev => prev.filter(s => s !== fieldName));
    setSatirlar(prev => prev.filter(s => s !== fieldName));
    setMevcutAlanlar(prev => prev.filter(s => s !== fieldName));
  };

  const appendFieldToFormula = (fieldName) => {
    setCalcDraft(prev => ({
      ...prev,
      formulaText: `${prev.formulaText}${prev.formulaText ? " " : ""}[${fieldName}]`
    }));
  };

  const handleAddConditionalFormat = () => {
    if (!formatDraft.field) return alert("Biçimlendirilecek alanı seçin.");
    const needsValue = ["greaterThan", "lessThan", "equal", "between"].includes(formatDraft.type);
    if (needsValue && formatDraft.value === "") return alert("Karşılaştırma değeri girin.");
    if (formatDraft.type === "between" && formatDraft.value2 === "") return alert("İkinci aralık değerini girin.");
    const needsN = ["topN", "bottomN"].includes(formatDraft.type);
    if (needsN && (!Number(formatDraft.n) || Number(formatDraft.n) < 1)) return alert("Geçerli bir sıra adedi girin.");

    setConditionalFormats(prev => [...prev, {
      id: makeCalcId(),
      field: formatDraft.field,
      type: formatDraft.type,
      value: formatDraft.value,
      value2: formatDraft.value2,
      n: Number(formatDraft.n) || 3,
      style: {
        backgroundColor: formatDraft.backgroundColor,
        color: formatDraft.color,
        fontWeight: 700
      }
    }]);
  };

  const handleRemoveConditionalFormat = (id) => {
    setConditionalFormats(prev => prev.filter(rule => rule.id !== id));
  };

  const normalizeColorValue = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
    const rgb = raw.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    if (!rgb) return raw;
    const parts = rgb.slice(1).map(v => Math.max(0, Math.min(255, Number(v))));
    return `#${parts.map(v => v.toString(16).padStart(2, "0")).join("")}`;
  };

  const renderColorPalette = (label, field, textField = null) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", marginBottom: 5 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 18px)", gap: 4, marginBottom: 6 }}>
        {FORMAT_COLOR_PALETTE.map(color => (
          <button
            key={`${field}-${color.value}`}
            type="button"
            title={`${label}: ${color.label} (${color.value})`}
            onClick={() => setFormatDraft(prev => ({
              ...prev,
              [field]: color.value,
              ...(textField ? { [textField]: color.text } : {})
            }))}
            style={{
              width: 18,
              height: 18,
              borderRadius: 3,
              border: formatDraft[field] === color.value ? "2px solid #0f172a" : "1px solid #cbd5e1",
              background: color.value,
              cursor: "pointer",
              padding: 0
            }}
          />
        ))}
      </div>
      <input
        title={`${label} RGB veya HEX degeri`}
        value={formatDraft[field]}
        onChange={e => setFormatDraft(prev => ({ ...prev, [field]: normalizeColorValue(e.target.value) }))}
        placeholder="#ffffff"
        style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 11 }}
      />
    </div>
  );

  const formatCellValue = (fieldName, value) => {
    const calc = calculatedFields.find(f => f.name === fieldName);
    if (typeof value !== "number") return displayValue(value) || "-";
    const decimals = calc ? Number(calc.decimals ?? 2) : 2;
    const formatted = value.toLocaleString("tr-TR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    if (calc?.format === "percent") return `${formatted}%`;
    if (calc?.format === "currency") return `${formatted} TL`;
    return formatted;
  };

  const getConditionalCellStyle = (fieldName, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return {};
    const fieldRules = conditionalFormats.filter(rule => rule.field === fieldName);
    if (fieldRules.length === 0) return {};
    const values = pivotData
      .map(row => Number(row[fieldName]))
      .filter(v => Number.isFinite(v));
    const average = values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
    const sortedDesc = [...values].sort((a, b) => b - a);
    const sortedAsc = [...values].sort((a, b) => a - b);

    for (const rule of fieldRules) {
      const v1 = toNumber(rule.value);
      const v2 = toNumber(rule.value2);
      const n = Number(rule.n) || 3;
      let match = false;

      if (rule.type === "greaterThan") match = numericValue > v1;
      if (rule.type === "lessThan") match = numericValue < v1;
      if (rule.type === "equal") match = numericValue === v1;
      if (rule.type === "between") match = numericValue >= Math.min(v1, v2) && numericValue <= Math.max(v1, v2);
      if (rule.type === "aboveAverage") match = numericValue > average;
      if (rule.type === "belowAverage") match = numericValue < average;
      if (rule.type === "topN") {
        const threshold = sortedDesc[Math.min(n, sortedDesc.length) - 1];
        match = threshold !== undefined && numericValue >= threshold;
      }
      if (rule.type === "bottomN") {
        const threshold = sortedAsc[Math.min(n, sortedAsc.length) - 1];
        match = threshold !== undefined && numericValue <= threshold;
      }

      if (match) return rule.style || {};
    }

    return {};
  };

  const excelIndir = () => {
    const veri = pivotData.map(row => {
      let obj = {};
      satirlar.forEach(s => obj[getGörünenAd(s)] = displayValue(row[s]));
      sutunlar.forEach(s => obj[getGörünenAd(s)] = displayValue(row[s]));
      if(mazotGerekliMi) obj["Mazot Fiyatı"] = row.Mazot;
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(veri);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rapor");
    XLSX.writeFile(wb, "Hakedis_Raporu.xlsx");
  };

const renderFilterMenu = (col) => {
const allValues = Array.from(new Set(hamVeri.map(item => String(pivotGroupValue(col, item[col])))))
  .filter(v => v.toUpperCase().includes(filterSearch.toUpperCase()))
  .sort((a, b) => a.localeCompare(b, "tr", { numeric: true, sensitivity: 'base' })); // numeric: true rakamları doğru sıralar (1, 5, 10...)

    const isAllSelected = allValues.length > 0 && allValues.every(v => filters[col]?.includes(v));

    return (
      <div 
        ref={filterRef} 
        style={{ 
          position: "absolute", 
          top: "100%", // Başlığın tam altından başlar
          left: "0",
          backgroundColor: "white", 
          border: "2px solid #93c5fd", // Daha belirgin sınır
          padding: "12px", 
          zIndex: 999999, 
          borderRadius: "8px", 
          boxShadow: "0 15px 30px rgba(0,0,0,0.2)", 
          minWidth: "240px",
          marginTop: "5px"
        }}
      >
        {/* Sıralama Butonu */}
        <button 
          onClick={() => setSortedBy({ col, asc: sortedBy.col === col ? !sortedBy.asc : true })}
          style={{ width: "100%", padding: "8px", marginBottom: "10px", cursor: "pointer", fontWeight: "bold", background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "11px" }}
        >
          {sortedBy.col === col && !sortedBy.asc ? "↓ Z'den A'ya Sırala" : "↑ A'dan Z'ye Sırala"}
        </button>

        {/* Arama Girişi */}
        <input 
          placeholder="Ara..." 
          value={filterSearch} 
          onChange={e => setFilterSearch(e.target.value)}
          style={{ width: "100%", marginBottom: "10px", padding: "8px", borderRadius: "4px", border: "1px solid #ddd", fontSize: "12px", boxSizing: "border-box" }}
        />

        {/* Tümünü Seç Grid Yapısı */}
        <label style={{ display: "grid", gridTemplateColumns: "30px 1fr", alignItems: "center", marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #f1f5f9", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
          <input 
            type="checkbox" 
            checked={isAllSelected} 
            onChange={() => setFilters(p => ({ ...p, [col]: isAllSelected ? [] : allValues }))} 
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span style={{ marginLeft: "4px" }}>Tümünü Seç / Kaldır</span>
        </label>

        {/* Değer Listesi */}
        <div style={{ maxHeight: "200px", overflowY: "auto", overflowX: "hidden" }}>
          {allValues.map(val => (
            <label key={val} style={{ display: "grid", gridTemplateColumns: "30px 1fr", alignItems: "center", padding: "5px 0", cursor: "pointer", fontSize: "11px" }}>
              <input 
                type="checkbox" 
                checked={filters[col]?.includes(val) || false} 
                onChange={() => {
                  const current = filters[col] || [];
                  const next = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
                  setFilters({ ...filters, [col]: next });
                }} 
                style={{ width: "14px", height: "14px", cursor: "pointer" }}
              />
              <span style={{ marginLeft: "4px", textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{val}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div style={{ padding: 25, background: "#f1f5f9", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      
{/* Filtre Paneli */}
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 15, background: "white", padding: 20, borderRadius: 15, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", marginBottom: 20 }}>
  
  {/* DÖNEM (AYLAR) */}
  <div>
    <label style={{ fontSize: 11, fontWeight: 700 }}>DÖNEM</label>
    <select 
      multiple 
      value={seciliAylar} // <--- BU SATIRI EKLEDİK (Tasarım seçince kutu otomatik dolsun diye)
      style={{ width: "100%", height: 80, marginTop: 5, borderRadius: 8, border: "1px solid #e2e8f0" }} 
      onChange={e => setSeciliAylar(Array.from(e.target.selectedOptions, r => r.value))}
    >
      {sonAylar.map(a => <option key={a} value={a}>{a}</option>)}
    </select>
  </div>

  {/* HAFTA */}
  <div>
    <label style={{ fontSize: 11, fontWeight: 700 }}>HAFTA</label>
    <select 
      multiple 
      value={seciliHaftalar} // <--- BU SATIRI EKLEDİK (Tasarım seçince kutu otomatik dolsun diye)
      style={{ width: "100%", height: 80, marginTop: 5, borderRadius: 8, border: "1px solid #e2e8f0" }} 
      onChange={e => setSeciliHaftalar(Array.from(e.target.selectedOptions, r => r.value))}
    >
      {[1,2,3,4,5].map(h => <option key={h} value={h.toString()}>{h}. Hafta</option>)}
    </select>
  </div>

  {/* HIZLI TARİH SEÇİMİ (Zaten eklemiştin) */}
  <div>
    <label style={{ fontSize: 11, fontWeight: 700 }}>HIZLI TARİH</label>
    <select 
      value={preset} 
      onChange={(e) => applyHakedisPreset(e.target.value)} 
      style={{ width: "100%", height: 35, marginTop: 5, borderRadius: 8, border: "1px solid #e2e8f0" }}
    >
      <option value="OZEL">Özel Seçim</option>
      {HAKEDIS_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
    </select>
  </div>

{/* TASARIM SEÇİMİ VE KAYIT (MazotListesi Tarzı) */}
  <div>
    <label style={{ fontSize: 11, fontWeight: 700 }}>RAPOR TIPI</label>
    <select
      value={reportScope}
      onChange={e => { setReportScope(e.target.value); setHamVeri([]); setDatasetInfo(null); }}
      style={{ width: "100%", height: 35, marginTop: 5, borderRadius: 8, border: "1px solid #e2e8f0" }}
      title="Toplam KM ve bagli hesaplarin hangi bakis acisiyla dagitilacagini belirler."
    >
      <option value="personel">Personel Bazli</option>
      <option value="plaka">Plaka Bazli</option>
      <option value="tesis">Tesis Bazli</option>
    </select>
  </div>

<div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, alignItems: "center", paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
  <span style={{ fontSize: 12, fontWeight: "bold" }}>Tasarım:</span>
  <select value={selectedDesignId} onChange={(e) => applyDesign(e.target.value)} style={{ padding: "6px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: 12, minWidth: 200 }}>
    <option value="">-- Kayıtlı Tasarım Seç --</option>
    {designs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
  </select>
  <input type="text" placeholder="Yeni Tasarım Adı..." value={reportName} onChange={e => setReportName(e.target.value)} style={{ padding: "6px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: 12 }} />
  <button onClick={handleSaveDesign} style={{ background: "#2563eb", color: "white", padding: "6px 15px", borderRadius: "6px", cursor: "pointer", border: "none", fontSize: 12 }}>Tasarımı Kaydet</button>
</div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 700 }}>SÖZLEŞME</label>
          <select multiple value={seciliSozlesmeIds} style={{ width: "100%", height: 80, marginTop: 5, borderRadius: 8, border: "1px solid #e2e8f0" }} onChange={e => setSeciliSozlesmeIds(Array.from(e.target.selectedOptions, r => r.value))}>
            {sozlesmeler.map(s => <option key={s.id} value={s.id}>{s.baslik}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button onClick={veriGetir} disabled={loading} style={{ background: "#1e293b", color: "white", padding: "15px 30px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 600 }}>
            {loading ? "GELİYOR..." : "HESAPLA"}
          </button>
        </div>
      </div>

{/* Alan Yönetimi */}
<div style={{ background: "white", padding: 15, borderRadius: 15, border: "1px solid #e2e8f0", marginBottom: 20 }}>
  <h4 style={{ fontSize: 12, marginBottom: 12, color: "#334155" }}>HESAPLANMIŞ ALANLAR</h4>
  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 120px 90px auto", gap: 8, alignItems: "center" }}>
    <input type="text" placeholder="Alan adı" value={calcDraft.name} onChange={e => setCalcDraft(prev => ({ ...prev, name: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    <input type="text" placeholder="[Mazot Litre] / [Toplam KM] * 100" value={calcDraft.formulaText} onChange={e => setCalcDraft(prev => ({ ...prev, formulaText: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    <select value={calcDraft.format} onChange={e => setCalcDraft(prev => ({ ...prev, format: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}>
      <option value="number">Sayı</option>
      <option value="percent">Yüzde</option>
      <option value="currency">Para</option>
    </select>
    <input type="number" min="0" max="6" value={calcDraft.decimals} onChange={e => setCalcDraft(prev => ({ ...prev, decimals: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    <button onClick={handleAddCalculatedField} style={{ background: "#7c3aed", color: "white", padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>Ekle</button>
  </div>
  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
    <select onChange={e => { if (e.target.value) appendFieldToFormula(e.target.value); e.target.value = ""; }} style={{ padding: 7, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12, minWidth: 220 }}>
      <option value="">Formüle alan ekle</option>
      {hesapAlanlari.map(a => <option key={a} value={a}>{getGörünenAd(a)}</option>)}
    </select>
    {["+", "-", "*", "/", "(", ")"].map(op => (
      <button key={op} onClick={() => setCalcDraft(prev => ({ ...prev, formulaText: `${prev.formulaText}${prev.formulaText ? " " : ""}${op}` }))} style={{ width: 32, height: 30, borderRadius: 6, border: "1px solid #cbd5e1", background: "#f8fafc", cursor: "pointer" }}>{op}</button>
    ))}
  </div>
  {calculatedFields.length > 0 && (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
      {calculatedFields.map(field => (
        <span key={field.id || field.name} style={{ background: "#faf5ff", padding: "5px 9px", borderRadius: 6, fontSize: 11, border: "1px solid #ddd6fe", display: "flex", alignItems: "center", gap: 6 }}>
          <b>{field.name}</b>
          <span style={{ color: "#64748b" }}>{field.formulaText}</span>
          <button onClick={() => handleRemoveCalculatedField(field.name)} style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer", fontWeight: 700 }}>x</button>
        </span>
      ))}
    </div>
  )}
</div>

<div style={{ background: "white", padding: 15, borderRadius: 15, border: "1px solid #e2e8f0", marginBottom: 20 }}>
  <h4 style={{ fontSize: 12, marginBottom: 12, color: "#334155" }}>KOSULLU BICIMLENDIRME</h4>
  <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.4fr) minmax(165px, 1fr) 110px 110px 78px 112px 112px 72px", gap: 8, alignItems: "start" }}>
    <select value={formatDraft.field} onChange={e => setFormatDraft(prev => ({ ...prev, field: e.target.value }))} style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}>
      <option value="">Alan sec</option>
      {sutunlar.map(a => <option key={a} value={a}>{getGörünenAd(a)}</option>)}
    </select>
    <select value={formatDraft.type} onChange={e => setFormatDraft(prev => ({ ...prev, type: e.target.value }))} style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}>
      <option value="greaterThan">Buyukse</option>
      <option value="lessThan">Kucukse</option>
      <option value="equal">Esitse</option>
      <option value="between">Arasindaysa</option>
      <option value="aboveAverage">Ortalama ustu</option>
      <option value="belowAverage">Ortalama alti</option>
      <option value="topN">Ilk N</option>
      <option value="bottomN">Son N</option>
    </select>
    <input type="text" placeholder="Deger" value={formatDraft.value} onChange={e => setFormatDraft(prev => ({ ...prev, value: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    <input type="text" placeholder="Deger 2" value={formatDraft.value2} onChange={e => setFormatDraft(prev => ({ ...prev, value2: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    <input type="number" min="1" value={formatDraft.n} onChange={e => setFormatDraft(prev => ({ ...prev, n: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }} />
    {renderColorPalette("Zemin", "backgroundColor", "color")}
    {renderColorPalette("Yazi", "color")}
    <button onClick={handleAddConditionalFormat} style={{ width: 72, background: "#0f766e", color: "white", padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>Ekle</button>
  </div>
  {conditionalFormats.length > 0 && (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
      {conditionalFormats.map(rule => (
        <span key={rule.id} style={{ background: rule.style?.backgroundColor || "#f8fafc", color: rule.style?.color || "#334155", padding: "5px 9px", borderRadius: 6, fontSize: 11, border: "1px solid #cbd5e1", display: "flex", alignItems: "center", gap: 6 }}>
          <b>{getGörünenAd(rule.field)}</b>
          <span>{rule.type}</span>
          <button onClick={() => handleRemoveConditionalFormat(rule.id)} style={{ border: "none", background: "transparent", color: "inherit", cursor: "pointer", fontWeight: 700 }}>x</button>
        </span>
      ))}
    </div>
  )}
</div>

<div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 15, marginBottom: 20 }}>
  
  {/* 📊 TÜM ALANLAR - ESNEK YAPI */}
  <div style={{ 
    background: "white", 
    padding: 15, 
    borderRadius: 15, 
    border: "1px solid #e2e8f0", 
    display: "flex", 
    flexDirection: "column",
    height: "auto", // Sabit yüksekliği kaldırdık
    minHeight: "80px" // Boşken çok ince görünmemesi için
  }}>
    <h4 style={{ fontSize: 12, marginBottom: 10, color: "#64748b" }}>📊 TÜM ALANLAR</h4>
    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10, lineHeight: 1.4 }}>
      {seciliSozlesmeIds.length > 0
        ? `${raporAlanlari.length} kullanilabilir alan listelendi. Secili sozlesmelerin tamaminda olmayan alanlar gizlendi.`
        : "Alan listesi sozlesme secimine gore daralir."}
      {datasetInfo ? ` Hazir dataset: ${datasetInfo.rowCount} satir / ${datasetInfo.fieldCount} alan.` : ""}
    </div>
    
    <input 
      type="text" 
      placeholder="Alan ara..." 
      style={{
        width: "calc(100% - 10px)",
        padding: "8px",
        marginBottom: "12px",
        borderRadius: "8px",
        border: "1px solid #cbd5e1",
        fontSize: "12px",
        outline: "none"
      }}
      onChange={(e) => setAlanAra(e.target.value.toLowerCase())}
    />

    <div style={{ 
      display: "flex", 
      gap: 6, 
      flexWrap: "wrap",
      maxHeight: "300px", // Çok dolarsa sayfa taşmasın diye sadece iç kaydırma
      overflowY: "auto" 
    }}>
      {raporAlanlari
        .filter(a => !satirlar.includes(a) && !sutunlar.includes(a))
        .filter(a => String(a).toLowerCase().includes(alanAra))
        .map(a => {
          const measure = isFieldUsableForMeasures(a);
          return (
        <div key={a} title={measure ? "" : "Bu alan hesaplanabilir deger alani degil; satir/grup olarak kullanin."} style={{ background: measure ? "#f1f5f9" : "#fff7ed", borderRadius: 6, display: "flex", border: measure ? "1px solid #cbd5e1" : "1px solid #fdba74", overflow: "hidden", height: "fit-content" }}>
          <span style={{ padding: "4px 8px", fontSize: 11, whiteSpace: "nowrap" }}>{getGörünenAd(a)}</span>
          <button onClick={() => setSatirlar([...satirlar, canonicalFieldName(a)])} style={{ background: "#2563eb", color: "white", border: "none", padding: "0 8px", cursor: "pointer" }}>S</button>
          <button onClick={() => addMeasureField(a)} style={{ background: measure ? "#059669" : "#f97316", color: "white", border: "none", padding: "0 8px", cursor: "pointer" }}>K</button>
        </div>
      );})}
    </div>
  </div>

  {/* SATIRLAR VE SÜTUNLAR KUTULARI DA AYNI ŞEKİLDE height: "auto" OLMALI */}
  <div style={{ background: "#eff6ff", padding: 15, borderRadius: 15, border: "1px solid #bfdbfe", height: "auto", minHeight: "80px" }}>
    <h4 style={{ fontSize: 12, marginBottom: 10, color: "#1e40af" }}>↕ SATIRLAR (Grup)</h4>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {satirlar.map(s => {
        const usable = isFieldUsableForRows(s);
        const meta = raporAlanMeta.get(s);
        return (
        <span key={s} title={!usable ? "Bu alan secili sozlesmelerde bulunmuyor." : (meta?.partial ? "Bu alan secili sozlesmelerin tamaminda yok." : "")} style={{ background: usable ? (meta?.partial ? "#eff6ff" : "white") : "#fff7ed", padding: "4px 10px", borderRadius: 6, fontSize: 11, border: usable ? (meta?.partial ? "1px solid #93c5fd" : "1px solid #bfdbfe") : "1px solid #fdba74", display: "flex", alignItems: "center" }}>
          {getGörünenAd(s)} <b onClick={() => setSatirlar(satirlar.filter(x => x !== s))} style={{ marginLeft: 8, cursor: "pointer", color: "#ef4444" }}>✕</b>
        </span>
      );})}
    </div>
  </div>

  <div style={{ background: "#f0fdf4", padding: 15, borderRadius: 15, border: "1px solid #bbf7d0", height: "auto", minHeight: "80px" }}>
    <h4 style={{ fontSize: 12, marginBottom: 10, color: "#166534" }}>↔ SÜTUNLAR (Değer)</h4>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {sutunlar.map(s => {
        const measure = isFieldUsableForMeasures(s);
        return (
        <span key={s} title={measure ? "" : "Bu alan hesaplanabilir deger alani degil."} style={{ background: measure ? "white" : "#fff7ed", padding: "4px 10px", borderRadius: 6, fontSize: 11, border: measure ? "1px solid #bbf7d0" : "1px solid #fdba74", display: "flex", alignItems: "center" }}>
          {getGörünenAd(s)} <b onClick={() => setSutunlar(sutunlar.filter(x => x !== s))} style={{ marginLeft: 8, cursor: "pointer", color: "#ef4444" }}>✕</b>
        </span>
      );})}
    </div>
  </div>
</div>

      {/* Rapor Tablosu */}
      <div style={{ background: "white", borderRadius: 15, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}>
        <div style={{ padding: 20, borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontSize: 16 }}>Pivot Rapor Önizleme</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label title="Ara toplam satirlarini gosterir. Kapaliyken rapor daha hizli hesaplanir." style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155", fontWeight: 600 }}>
              <input type="checkbox" checked={showSubtotals} onChange={e => setShowSubtotals(e.target.checked)} />
              Ara toplamlari goster
            </label>
          <button onClick={excelIndir} style={{ background: "#059669", color: "white", padding: "10px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600 }}>İNDİR (EXCEL)</button>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead style={{ background: "#f8fafc" }}>
              <tr>
               {satirlar.map(s => (
  <th key={s} style={{ padding: 15, fontSize: 12, color: "#64748b", borderBottom: "2px solid #e2e8f0", position: "relative" }}> 
    
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      {/* 1. Kolonun Adı */}
      {getGörünenAd(s)}

      {/* 2. Filtre Butonu (Ok işareti) */}
      <span 
        onClick={() => { 
          setOpenFilter(openFilter === s ? null : s); 
          setFilterSearch(""); 
        }} 
        style={{ cursor: "pointer", marginLeft: 8, color: filters[s]?.length > 0 ? "#2563eb" : "#94a3b8", fontSize: 10 }}
      >
        ▼
      </span>
    </div>

    {/* 3. Gizli Menü (Açılır Kutu) */}
    {openFilter === s && renderFilterMenu(s)}
  </th>
))}
                {sutunlar.map(s => <th key={s} style={{ padding: 15, fontSize: 12, color: "#64748b", borderBottom: "2px solid #e2e8f0" }}>{getGörünenAd(s)}</th>)}
                {mazotGerekliMi && <th style={{ padding: 15, fontSize: 12, background: "#fef3c7", borderBottom: "2px solid #e2e8f0" }}>MAZOT FİYATI</th>}
              </tr>
{/* Başlık Satırı Bitişi */}
</thead>

{/* YENİ: Başlığın hemen altındaki TOPLAM satırı */}
<thead style={{ background: "#f8fafc", fontWeight: "bold", borderBottom: "2px solid #cbd5e1" }}>
  <tr>
    <td colSpan={satirlar.length} style={{ padding: "10px", textAlign: "right", color: "#64748b", fontSize: "11px" }}></td>
    {sutunlar.map(s => (
      <td key={s} style={{ padding: "10px", color: "#1e293b", borderBottom: "1px solid #e2e8f0" }}>
        {formatCellValue(s, pivotData.filter(row => !row._isSubtotal).reduce((acc, curr) => acc + (Number(curr[s]) || 0), 0))}
      </td>
    ))}
  </tr>
</thead>

<tbody>
{/* Veri satırları buraya gelecek */}         
              {pivotData.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", background: row._isSubtotal ? "#f8fafc" : undefined }}>
                  {satirlar.map(s => <td key={s} style={{ padding: 15, fontSize: 13, fontWeight: row._isSubtotal ? 800 : 600 }}>{displayValue(row[s]) || "-"}</td>)}
                  {sutunlar.map(s => (
                    <td key={s} style={{ padding: 15, fontSize: 13, fontWeight: row._isSubtotal ? 800 : undefined, ...getConditionalCellStyle(s, row[s]) }}>
                      {formatCellValue(s, row[s])}
                    </td>
                  ))}
                  {mazotGerekliMi && (
                    <td style={{ padding: 10, background: "#fffbeb" }}>
                      <input 
                        type="number" 
                        value={row.Mazot === 0 ? "" : row.Mazot} 
                        placeholder="0.00"
                        onChange={(e) => handleMazotChange(idx, e.target.value)}
                        style={{ width: 80, padding: 8, borderRadius: 6, border: "1px solid #fcd34d" }} 
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            
          </table>
        </div>
      </div>
    </div>
  );
};

export default HakedisPivotEngine;
